import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, MessageCircle, MoreVertical, X, Users, Users2, Hash, Target, CalendarCheck, Activity, Megaphone, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import type { ClubChannel, ClubChannelMessage } from '@/types/club-community';
import { useClubCommunityOptimized } from '@/hooks/useClubCommunityOptimized';
import { clubCommunityService } from '@/services/api/club-community.service';
import { EmptyState } from '@/components/ui/EmptyState';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { UnifiedMessagingService } from '@/services/api/unified-messaging.service';
import { useUnreadMessageCount } from '@/hooks/useUnreadMessageCount';
import { formatDistanceToNow } from 'date-fns';
import { fr, nl } from 'date-fns/locale';
import type { UnifiedMessage, UnifiedConversation } from '@/types/unified-messaging';
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { UnifiedMessageBubble } from './UnifiedMessageBubble';
import { UnifiedMessageInput, type UnifiedMessageInputHandle } from './UnifiedMessageInput';
import { AnnouncementObserverRow } from './AnnouncementObserverRow';
import { AnnouncementThreadSheet } from './AnnouncementThreadSheet';
import { MessageContextMenu } from './MessageContextMenu';
import { MentionAutocomplete } from '@/components/ui/mention-autocomplete';
import { PhotoUploadPreview } from './PhotoUploadPreview';
import { ConversationHeader } from './ConversationHeader';
import { GroupInfoModal } from './GroupInfoModal';
import { useMessageActions } from '@/hooks/messaging/useMessageActions';
import { useMessageInput } from '@/hooks/messaging/useMessageInput';
import { useRealTimeMessages } from '@/hooks/messaging/useRealTimeMessages';
import { useOfflineMessages } from '@/hooks/messaging/useOfflineMessages';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import { hapticMedium } from '@/lib/haptics';
import { toast } from 'sonner';
import { throttle } from '@/utils/debounce';
import { useModalBodyFlag } from '@/hooks/useModalBodyFlag';

interface UnifiedChatInterfaceProps {
  conversationId?: string;
  conversationType?: 'channel' | 'direct';
  /** Override default back navigation (defaults to handleBack()) */
  onBack?: () => void;
  /** When true, uses h-full instead of h-screen so the chat fits inside a parent container */
  embedded?: boolean;
  /** Admin-only: when provided, a "Convert to Task" option appears in the message context menu */
  onConvertToTask?: (message: { id: string; content: string; sender_name?: string }) => void;
  /** When provided, replaces the derived conversation title shown in the header */
  overrideName?: string;
  /** When true, hides the ConversationHeader entirely (use when a parent panel already has a header) */
  hideHeader?: boolean;
  /** Pre-fills the message composer on mount (e.g. "Question about: Exercise Name — ") */
  initialMessageText?: string;
  /** When set, first send will be an exercise_share card; clears after sending */
  pendingExerciseData?: Record<string, unknown>;

  // Channel mode (present = club channel surface, absent = DM surface)
  channels?: ClubChannel[];
  activeChannelId?: string | null;
  onChannelSwitch?: (channelId: string) => void;
  clubId?: string;
}

const CHANNEL_ICONS: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  text: Hash,
  advice: Target,
  events: CalendarCheck,
  round_shares: Activity,
  announcements: Megaphone,
};

function sameGroup(a: UnifiedMessage, b: UnifiedMessage): boolean {
  if (a.sender_id !== b.sender_id) return false;
  return Math.abs(new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) < 5 * 60 * 1000;
}

function adaptChannelMessage(msg: ClubChannelMessage): UnifiedMessage {
  return {
    id: msg.id,
    conversation_id: msg.channel_id,
    channel_id: msg.channel_id,
    sender_id: msg.sender_id,
    content: msg.content ?? '',
    message_type: (msg.message_type as any) ?? 'text',
    message_data: msg.message_data as any,
    created_at: msg.created_at,
    updated_at: msg.edited_at ?? msg.created_at,
    edited_at: msg.edited_at,
    reply_to_id: msg.reply_to_id ?? null as any,
    reply_to: msg.reply_to ? adaptChannelMessage(msg.reply_to) : (null as any),
    reaction_counts: msg.reaction_counts ?? {},
    user_has_reacted: msg.user_has_reacted ?? {},
    sender: msg.sender ?? null as any,
    message_context: 'channel' as const,
    // Extra fields accessed by channel-mode render path
    ...(msg.is_admin_message !== undefined && { is_admin_message: msg.is_admin_message } as any),
    ...(msg.admin_message_type !== undefined && { admin_message_type: msg.admin_message_type } as any),
    ...(msg.is_pinned !== undefined && { is_pinned: msg.is_pinned } as any),
    ...(msg.thread_root_id !== undefined && { thread_root_id: msg.thread_root_id } as any),
  };
}

export function UnifiedChatInterface({ conversationId: propConversationId, conversationType: propConversationType, onBack: propOnBack, embedded = false, onConvertToTask, overrideName, hideHeader = false, initialMessageText, pendingExerciseData: initialPendingExercise, channels, activeChannelId, onChannelSwitch, clubId }: UnifiedChatInterfaceProps) {
  const { t, language } = useAppTranslations();

  // Always call — hooks can't be conditional.
  // When clubId is absent the hook returns empty/no-op data.
  const channelData = useClubCommunityOptimized({
    clubId: clubId ?? '',
    autoSubscribe: !!clubId,
  });

  // Channel mode: either the caller pre-fetched channels (legacy path) or the hook fetched them.
  const effectiveChannels = (channels && channels.length > 0) ? channels : channelData.channels;
  const isChannelMode = !!(effectiveChannels.length > 0 && clubId);

  // Helper function to format dates with proper locale
  const formatDateWithLocale = (date: Date) => {
    const localeMap = { fr, nl, en: undefined };
    return formatDistanceToNow(date, { addSuffix: true, locale: localeMap[language as keyof typeof localeMap] });
  };
  
  const navigate = useNavigate();
  const params = useParams();
  const { user } = useAuth();
  const { refreshUnreadCount } = useUnreadMessageCount();
  const queryClient = useQueryClient();

  const handleBack = propOnBack || (() => navigate(-1));

  const service = UnifiedMessagingService.getInstance();
  const MESSAGES_PER_PAGE = 30;

  // State — conversation declared early so hooks below can reference it safely
  const [conversation, setConversation] = useState<UnifiedConversation | null>(null);
  const [messages, setMessages] = useState<UnifiedMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [conversationType, setConversationType] = useState<'channel' | 'direct'>('direct');
  
  // Pagination state
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  
  // Message interaction state
  const [showReactionPicker, setShowReactionPicker] = useState<string | null>(null);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [longPressedMessage, setLongPressedMessage] = useState<string | null>(null);
  const [tappedMessageForTimestamp, setTappedMessageForTimestamp] = useState<string | null>(null);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [editingGroupName, setEditingGroupName] = useState(false);
  const [groupNameInput, setGroupNameInput] = useState('');
  const [showAddParticipants, setShowAddParticipants] = useState(false);
  const [participantSearchQuery, setParticipantSearchQuery] = useState('');
  const [participantSearchResults, setParticipantSearchResults] = useState<any[]>([]);
  const [searchingParticipants, setSearchingParticipants] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [messagePosition, setMessagePosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showClearChatConfirm, setShowClearChatConfirm] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showChannelSidebar, setShowChannelSidebar] = useState(false);
  const [showMembersDropdown, setShowMembersDropdown] = useState(false);
  const [showChannelSearch, setShowChannelSearch] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [virtualScrollTop, setVirtualScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(600);
  const scrollStateThrottleRef = useRef<((...args: any[]) => void) | null>(null);
  const [activeAnnouncementThread, setActiveAnnouncementThread] = useState<UnifiedMessage | null>(null);
  const [announcementReplyCounts, setAnnouncementReplyCounts] = useState<Record<string, { count: number; replierIds: string[] }>>({});

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<{ focus: () => void }>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const loadedConversationIdRef = useRef<string | null>(null);
  const dismissCooldownRef = useRef(false);
  const prevScrollHeightRef = useRef(0);
  const viewedAnnouncementsRef = useRef<Set<string>>(new Set());
  // True for a short window right after the menu opens, so the release of the
  // opening long-press doesn't immediately dismiss it (iMessage behaviour).
  const openCooldownRef = useRef(false);

  // Scroll handler for scroll-to-bottom button
  const handleScrollForButton = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollToBottom(!isNearBottom);
    }
  }, []);

  // Channel-mode scroll handler: triggers cursor-based pagination and virtual scroll tracking
  const handleChannelScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    setShowScrollToBottom(!isNearBottom);
    // Throttle the virtual-scroll state updates to one per frame (~16ms) to avoid
    // scheduling 2 setState calls per scroll tick at 60fps.
    if (!scrollStateThrottleRef.current) {
      scrollStateThrottleRef.current = throttle((scrollTop: number, height: number) => {
        setVirtualScrollTop(scrollTop);
        setContainerHeight(height);
      }, 16);
    }
    scrollStateThrottleRef.current(el.scrollTop, el.clientHeight);
    if (el.scrollTop < 80 && channelData.hasNextPage && !channelData.isFetchingNextPage) {
      prevScrollHeightRef.current = el.scrollHeight;
      channelData.fetchNextPage();
    }
  }, [channelData]);

  useModalBodyFlag(showGroupInfo);

  // In channel mode, use channel messages adapted to UnifiedMessage shape; otherwise use DM state
  // Filter out replies to announcements — those only appear in the thread sheet
  const activeMessages: UnifiedMessage[] = useMemo(() => {
    if (isChannelMode) {
      const announcementIds = new Set(
        channelData.messages
          .filter(m => !!(m.message_data as any)?.isAnnouncement)
          .map(m => m.id)
      );
      return channelData.messages
        .map(adaptChannelMessage)
        .filter(m => !m.reply_to_id || !announcementIds.has(m.reply_to_id));
    }
    return messages;
  }, [isChannelMode, channelData.messages, messages]);

  // Stable list of announcement IDs — only changes when the set of announcements changes,
  // not on every new chat message. Used to avoid firing a DB query per realtime insert.
  const channelAnnouncementIds = useMemo(
    () => channelData.messages
      .filter(m => !!(m.message_data as any)?.isAnnouncement)
      .map(m => m.id),
    [channelData.messages]
  );
  const channelAnnouncementIdsKey = channelAnnouncementIds.join(',');

  // Fetch reply counts for all announcements in the current channel
  useEffect(() => {
    if (!isChannelMode || !channelData.activeChannel || channelAnnouncementIds.length === 0) return;
    clubCommunityService.getAnnouncementReplyCounts(channelAnnouncementIds, channelData.activeChannel)
      .then(r => { if (r.success && r.data) setAnnouncementReplyCounts(r.data); });
  }, [isChannelMode, channelData.activeChannel, channelAnnouncementIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore scroll position after new pages load in channel mode (pagination)
  useEffect(() => {
    if (!isChannelMode || !messagesContainerRef.current || prevScrollHeightRef.current === 0) return;
    const el = messagesContainerRef.current;
    el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
    prevScrollHeightRef.current = 0;
  }, [activeMessages.length, isChannelMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom on initial channel load and when switching channels
  const initialScrollDoneRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isChannelMode) return;
    const channelId = channelData.activeChannel;
    if (!channelId || activeMessages.length === 0) return;
    // Only fire once per channel — don't fight the user scrolling up
    if (initialScrollDoneRef.current === channelId) return;
    initialScrollDoneRef.current = channelId;
    // Use instant (no animation) so it doesn't flash from top to bottom
    messagesEndRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
  }, [isChannelMode, channelData.activeChannel, activeMessages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowScrollToBottom(false);
  }, []);

  // For direct convos: who appears on the right (blue bubble). So we always get back-and-forth.
  const rightSideSenderId = useMemo(() => {
    const ids = conversation?.participants?.map((p: { id?: string }) => p.id).filter(Boolean) ?? [];
    const sorted = [...new Set(ids)].sort();
    if (sorted.length >= 2 && user?.id && sorted.includes(user.id)) return user.id;
    if (sorted.length === 2) return sorted[1];
    const fromMessages = [...new Set(messages.map((m) => m.sender_id).filter(Boolean))].sort();
    if (fromMessages.length === 2) return fromMessages[1];
    return user?.id ?? null;
  }, [conversation?.participants, user?.id, messages]);

  // Search functionality
  const filteredMessages = useMemo(() => {
    if (!searchQuery.trim()) return activeMessages;
    const query = searchQuery.toLowerCase();
    return activeMessages.filter(message =>
      message.content?.toLowerCase().includes(query) ||
      message.sender?.display_name?.toLowerCase().includes(query)
    );
  }, [activeMessages, searchQuery]);

  // Virtual scrolling constants
  const VIRTUAL_SCROLL_THRESHOLD = 100;
  const ESTIMATED_ROW_HEIGHT = 80;
  const VIRTUAL_BUFFER = 5;

  const useVirtualScroll = isChannelMode && filteredMessages.length > VIRTUAL_SCROLL_THRESHOLD;

  const visibleRange = useMemo(() => {
    if (!useVirtualScroll) return null;
    const startIndex = Math.max(0, Math.floor(virtualScrollTop / ESTIMATED_ROW_HEIGHT) - VIRTUAL_BUFFER);
    const endIndex = Math.min(
      filteredMessages.length - 1,
      Math.ceil((virtualScrollTop + containerHeight) / ESTIMATED_ROW_HEIGHT) + VIRTUAL_BUFFER
    );
    return { startIndex, endIndex };
  }, [useVirtualScroll, filteredMessages.length, virtualScrollTop, containerHeight]);

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      // Scroll to first matching message
      const firstMatch = filteredMessages[0];
      if (firstMatch) {
        const element = document.querySelector(`[data-message-id="${firstMatch.id}"]`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Sync external activeChannelId prop into the hook's internal state.
  // markChannelRead is handled inside the hook whenever activeChannel changes.
  useEffect(() => {
    if (!isChannelMode || !activeChannelId) return;
    channelData.setActiveChannel(activeChannelId);
  }, [isChannelMode, activeChannelId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click outside handler for more menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMoreMenu) {
        const target = event.target as Element;
        if (!target.closest('[data-more-menu]')) {
          setShowMoreMenu(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreMenu]);

  // Handle mute notifications
  const handleMuteNotifications = async () => {
    try {
      // TODO: Implement mute functionality with backend
      setIsMuted(!isMuted);
      setShowMoreMenu(false);
      
      // Show feedback
      const action = isMuted ? 'unmuted' : 'muted';

      // In a real app, you'd call an API to update the mute status
      // await UnifiedMessagingService.getInstance().muteConversation(conversation?.id, !isMuted);
    } catch (error) {
      console.error('❌ Error toggling mute:', error);
    }
  };

  // Handle clear chat
  const handleClearChat = async () => {
    try {
      setShowClearChatConfirm(false);
      setShowMoreMenu(false);
      
      if (!conversation?.id) {
        console.error('❌ No conversation ID available for clearing');
        return;
      }
      
      // Clear messages from UI immediately for better UX
      setMessages([]);
      
      // Call backend to delete/leave the conversation
      const response = await UnifiedMessagingService.getInstance().deleteConversation(conversation.id);

      if (response.success) {
        // Navigate back to messages list
        handleBack();
      } else {
        console.error('❌ Failed to clear conversation:', response.error);
        // Show error and restore messages by reloading them
        setError(response.error || 'Failed to clear conversation');
        loadMessages(1, true); // Reload messages
      }
    } catch (error) {
      console.error('❌ Error clearing chat:', error);
      setError('Failed to clear conversation');
      loadMessages(1, true); // Reload messages on error
    }
  };

  // Handle block user or leave channel
  const handleBlockOrLeave = async () => {
    try {
      setShowBlockConfirm(false);
      setShowMoreMenu(false);
      
      if (conversationType === 'direct') {
        // Block user functionality
        // TODO: Implement block user API call
        // await UnifiedMessagingService.getInstance().blockUser(otherParticipantId);
        
        // Navigate back to messages list
        handleBack();
      } else {
        // Leave channel functionality
        // TODO: Implement leave channel API call
        // await UnifiedMessagingService.getInstance().leaveChannel(conversation?.id);
        
        // Navigate back to messages list
        handleBack();
      }
    } catch (error) {
      console.error('❌ Error blocking/leaving:', error);
    }
  };

  // Show confirmation dialogs
  const showClearChatConfirmation = () => {
    setShowClearChatConfirm(true);
    setShowMoreMenu(false);
  };

  const showBlockConfirmation = () => {
    setShowBlockConfirm(true);
    setShowMoreMenu(false);
  };

  const currentConversationId = propConversationId || params.conversationId;

  // React Query for conversation metadata — instant on revisit
  const {
    data: _conversationData,
    isLoading: conversationLoading,
  } = useQuery({
    queryKey: ['conversation', currentConversationId],
    queryFn: () => service.getUnifiedConversation(currentConversationId!).then(r => {
      if (!r.success) throw new Error(r.error || 'Failed to load conversation');
      return r.data!;
    }),
    enabled: !!currentConversationId && !clubId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // React Query for messages — instant on revisit
  const {
    data: _messagesData,
    isLoading: messagesLoading,
  } = useQuery({
    queryKey: ['messages', currentConversationId],
    queryFn: () => service.getUnifiedMessages({ conversation_id: currentConversationId!, limit: MESSAGES_PER_PAGE, offset: 0 }).then(r => {
      if (!r.success) throw new Error(r.error || 'Failed to load messages');
      return [...(r.data || [])].reverse();
    }),
    enabled: !!currentConversationId && !clubId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // DM mode: loading until both conversation + messages arrive
  const loading = !clubId && (conversationLoading || messagesLoading);

  const currentConversationType = propConversationType || (conversation?.type === 'channel' ? 'channel' : 'direct');

  // Add message to UI immediately for optimistic update
  const addMessageToUI = useCallback((newMessage: UnifiedMessage) => {
    setMessages(prev => {
      const alreadyExists = prev.some(m => m.id === newMessage.id);
      if (alreadyExists) {
        return prev;
      }
      return [...prev, newMessage];
    });

    // Smooth scroll to bottom with animation
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }, 100);
  }, []);

  // Use custom hooks
  const offlineMessagesHook = useOfflineMessages({
    conversationId: currentConversationId || null,
    isOnline
  });

  const messageInputHook = useMessageInput({
    conversationId: currentConversationId || null,
    isOnline,
    queueOfflineMessage: offlineMessagesHook.queueOfflineMessage,
    addMessageToUI,
    setError
  });

  const messageActionsHook = useMessageActions({
    messages,
    setMessages,
    setShowReactionPicker,
    setLongPressedMessage,
    messageInputRef
  });

  // Extract values from hooks for easier access
  const {
    messageInput,
    setMessageInput,
    replyTo,
    setReplyTo,
    selectedPhotos,
    photoPreviewUrls,
    sending,
    uploading,
    uploadProgress,
    handlePhotoSelect,
    removePhoto,
    clearPhotos,
    handleSendMessage,
    handleSendPhotos,
    uploadMultiplePhotos,
  } = messageInputHook;

  // Seed composer with pre-filled text (e.g. from Ask Coach flow)
  useEffect(() => {
    if (initialMessageText) setMessageInput(initialMessageText);
  }, [initialMessageText]);

  // @mention selection: splice the selected username into the input
  const handleMentionSelect = useCallback((username: string, start: number, end: number) => {
    const before = messageInput.slice(0, start);
    const after = messageInput.slice(end);
    const newValue = `${before}@${username} ${after}`;
    setMessageInput(newValue);
    setCursorPosition(before.length + username.length + 2);
  }, [messageInput, setMessageInput]);

  // Exercise context attached to the next send
  const [pendingExercise, setPendingExercise] = useState<Record<string, unknown> | undefined>(initialPendingExercise);

  const {
    editingMessage,
    editContent,
    setEditContent,
    reactingMessages,
    handleReaction: handleReactionDM,
    startEditingMessage,
    cancelEditing,
    saveEditedMessage,
    deleteMessage,
    replyToMessage: replyToMessageHook
  } = messageActionsHook;

  // In channel mode, route reactions through the club community hook which
  // calls club_message_reactions and applies the correct optimistic cache update.
  const handleReaction = useCallback(async (messageId: string, emoji: string) => {
    if (!isChannelMode) {
      return handleReactionDM(messageId, emoji);
    }
    const message = channelData.messages.find(m => m.id === messageId);
    const hasReacted = message?.user_has_reacted?.[emoji];
    try {
      if (hasReacted) {
        await channelData.removeReaction(messageId, emoji);
      } else {
        await channelData.addReaction(messageId, emoji);
      }
    } catch (e) {
      console.error('Failed to update reaction:', e);
    }
    setLongPressedMessage(null);
    setMessagePosition(null);
  }, [isChannelMode, handleReactionDM, channelData, setLongPressedMessage]);

  // In channel mode, replyToMessageHook searches the DM messages array (always empty).
  // Override to look up from channelData.messages instead.
  const replyToMessage = useCallback((messageId: string) => {
    if (!isChannelMode) return replyToMessageHook(messageId);
    const msg = channelData.messages.find(m => m.id === messageId);
    if (!msg) return null;
    setLongPressedMessage(null);
    setMessagePosition(null);
    return adaptChannelMessage(msg);
  }, [isChannelMode, replyToMessageHook, channelData.messages]);

  // In channel mode, edit/delete must go to club_channel_messages, not UnifiedMessagingService.
  const handleEditMessage = useCallback((messageId: string, content: string) => {
    if (!isChannelMode) return startEditingMessage(messageId, content);
    // For channel mode: open inline edit by setting the editing state,
    // but save goes through clubCommunityService.updateMessage.
    startEditingMessage(messageId, content);
  }, [isChannelMode, startEditingMessage]);

  const handleSaveEdit = useCallback(async () => {
    if (!isChannelMode) return saveEditedMessage();
    if (!editingMessage || !editContent.trim()) return;
    const result = await clubCommunityService.updateMessage(editingMessage, { content: editContent.trim() });
    if (result.success) {
      // Invalidate TanStack Query cache so the updated message shows
      channelData.refetchMessages();
      cancelEditing();
    }
  }, [isChannelMode, saveEditedMessage, editingMessage, editContent, channelData, cancelEditing]);

  const handleDeleteMessage = useCallback(async (messageId: string) => {
    if (!isChannelMode) return deleteMessage(messageId);
    const result = await clubCommunityService.deleteMessage(messageId);
    if (result.success) {
      channelData.refetchMessages();
      setLongPressedMessage(null);
      setMessagePosition(null);
    }
  }, [isChannelMode, deleteMessage, channelData]);

  // Voice recording
  const voiceRecorder = useVoiceRecorder();
  const { uploadVoice, uploading: voiceUploading, uploadProgress: voiceUploadProgress } = useMediaUpload();
  const { uploadVideo, uploading: videoUploading, uploadProgress: videoUploadProgress } = useMediaUpload();

  // Video message handlers
  const handleVideoSelect = useCallback(async (file: File) => {
    if (!currentConversationId) return;
    try {
      const result = await uploadVideo(file);
      if (result) {
        await handleSendMessage(
          '',
          'video',
          { video_url: result.url, video_thumbnail_url: result.thumbnailUrl, video_provider: result.provider || 'r2' },
          replyTo?.id
        );
      }
    } catch (err: any) {
      console.error('Failed to send video:', err);
      toast.error(err?.message || 'Failed to send video');
    }
  }, [currentConversationId, uploadVideo, handleSendMessage, replyTo]);

  const handleVideoLinkSend = useCallback(async (url: string) => {
    if (!currentConversationId) return;
    try {
      const result = await uploadVideo(url);
      if (result) {
        await handleSendMessage(
          '',
          'video',
          { video_url: result.url, video_thumbnail_url: result.thumbnailUrl, video_provider: result.provider },
          replyTo?.id
        );
      }
    } catch (err: any) {
      console.error('Failed to send video link:', err);
      toast.error(err?.message || 'Failed to send video');
    }
  }, [currentConversationId, uploadVideo, handleSendMessage, replyTo]);

  const handleSendVoice = useCallback(async () => {
    if (!voiceRecorder.audioBlob) return;
    if (!isChannelMode && !currentConversationId) return;

    const blobType = voiceRecorder.audioBlob.type || 'audio/webm';
    // Pick extension that matches the actual mime type so R2/CDN serves correct Content-Type
    const ext = blobType.includes('mp4') || blobType.includes('m4a') ? 'm4a'
      : blobType.includes('ogg') ? 'ogg'
      : 'webm';
    const file = new File([voiceRecorder.audioBlob], `voice-${Date.now()}.${ext}`, {
      type: blobType,
    });
    const voiceDuration = voiceRecorder.duration;

    try {
      const voiceUrl = await uploadVoice(file);
      if (voiceUrl) {
        if (isChannelMode && channelData.activeChannel) {
          channelData.sendMessage({
            channel_id: channelData.activeChannel,
            content: '',
            message_type: 'voice',
            message_data: { voice_url: voiceUrl, voice_duration: voiceDuration, voice_mime_type: file.type },
            reply_to_id: replyTo?.id,
          });
        } else {
          await handleSendMessage(
            '',
            'voice',
            { voice_url: voiceUrl, voice_duration: voiceDuration, voice_mime_type: file.type },
            replyTo?.id
          );
        }
        voiceRecorder.clearRecording();
        setReplyTo(null);
      }
    } catch (err: any) {
      console.error('Failed to send voice message:', err);
      toast.error(err?.message || 'Failed to send voice message');
    }
  }, [voiceRecorder.audioBlob, voiceRecorder.duration, currentConversationId, isChannelMode, channelData, uploadVoice, handleSendMessage, replyTo, voiceRecorder.clearRecording]);

  const { offlineMessages, isRetrying, retryOfflineMessages } = offlineMessagesHook;

  // Handle new message from real-time subscription
  const handleNewMessage = useCallback((message: UnifiedMessage) => {
    // Skip messages from yourself - they're already shown via optimistic update
    if (message.sender_id === user?.id && !message.edited_at) {
      return;
    }

    setMessages(prev => {
      // Check if message already exists (prevent duplicates)
      const alreadyExists = prev.some(msg => msg.id === message.id);
      if (alreadyExists) {
        // If it exists and has been edited, update it
        if (message.edited_at) {
          return prev.map(msg => msg.id === message.id ? message : msg);
        }
        // Otherwise, skip adding duplicate
        return prev;
      }

      // Check if this is an update to an existing message (edit)
      if (message.edited_at) {
        return prev.map(msg => msg.id === message.id ? message : msg);
      }

      // Add new message (from other users) - ensure no duplicates
      return [...prev, message];
    });

    // Scroll to bottom for new messages
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, [user?.id]);

  useRealTimeMessages({
    conversationId: currentConversationId || null,
    conversation,
    currentUserId: user?.id,
    onNewMessage: handleNewMessage,
    setIsTyping
  });

  // Validate conversation ID
  const isValidConversationId = (id: string | undefined): boolean => {
    if (!id) return false;
    if (id === 'new') return false;
    // UUID validation regex
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  };

  // Load conversation details
  const loadConversation = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['conversation', currentConversationId] });
  }, [currentConversationId, queryClient]);

  // Load messages
  const loadMessages = useCallback(async (isInitialLoad = true, pageOverride?: number) => {
    if (!currentConversationId || !isValidConversationId(currentConversationId)) {
      console.error('❌ [UnifiedChatInterface] Invalid conversation ID in loadMessages:', currentConversationId);
      return;
    }

    try {
      if (isInitialLoad) {
        setPage(1);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const activePage = pageOverride !== undefined ? pageOverride : page;

      // Map conversation type to message loading type
      // Groups use 'direct' message loading, channels use 'channel' message loading
      const messageType = conversation?.type === 'channel' ? 'channel' : 'direct';
      const filters = messageType === 'channel'
        ? { channel_id: currentConversationId, limit: MESSAGES_PER_PAGE, offset: (activePage - 1) * MESSAGES_PER_PAGE }
        : { conversation_id: currentConversationId, limit: MESSAGES_PER_PAGE, offset: (activePage - 1) * MESSAGES_PER_PAGE };

      console.log('📨 [UnifiedChatInterface] Loading messages with filters:', filters);
      const response = await UnifiedMessagingService.getInstance().getUnifiedMessages(filters);
      
      console.log('📨 [UnifiedChatInterface] Messages response:', {
        success: response.success,
        messageCount: response.data?.length || 0,
        error: response.error,
        errorCode: response.errorCode
      });
      
      if (response.success && response.data) {
        const reversedMessages = response.data.reverse();
        console.log('✅ [UnifiedChatInterface] Setting messages:', reversedMessages.length, 'messages');

        if (isInitialLoad) {
          // Remove duplicates before setting
          const uniqueMessages = reversedMessages.filter((msg, index, self) =>
            index === self.findIndex(m => m.id === msg.id)
          );
          setMessages(uniqueMessages); // Replace messages for initial load
        } else {
          // Prepend for pagination, but filter out duplicates
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newUniqueMessages = reversedMessages.filter(msg => !existingIds.has(msg.id));
            return [...newUniqueMessages, ...prev];
          });
        }
        setHasMoreMessages(response.data.length === MESSAGES_PER_PAGE);
      } else {
        console.error('❌ [UnifiedChatInterface] Failed to load messages:', {
          error: response.error,
          errorCode: response.errorCode,
          statusCode: response.statusCode
        });
        setError(response.error || 'Failed to load messages');
        throw new Error(response.error || 'Failed to load messages');
      }
    } catch (err: any) {
      console.error('❌ [UnifiedChatInterface] Exception loading messages:', {
        error: err,
        message: err?.message,
        stack: err?.stack
      });
      setError(err?.message || 'Failed to load messages');
      throw err; // Re-throw so calling code knows it failed
    } finally {
      setLoadingMore(false);
    }
  }, [currentConversationId, conversation, conversationType, page, MESSAGES_PER_PAGE]);

  // Load more messages (pagination)
  const loadMoreMessages = useCallback(async () => {
    if (!hasMoreMessages || loadingMore) return;

    const nextPage = page + 1;
    setPage(nextPage);
    await loadMessages(false, nextPage);
  }, [hasMoreMessages, loadingMore, loadMessages, page]);

  // Offline message handling handled by useOfflineMessages hook

  // Handle scroll to load more messages
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const scrollTop = container.scrollTop;
    
    // Load more messages when scrolling to top
    if (scrollTop < 100 && hasMoreMessages && !loadingMore) {
      loadMoreMessages();
    }
  }, [hasMoreMessages, loadingMore, loadMoreMessages]);

  // Photo selection, sending, and message input handled by useMessageInput hook
  // handleNewMessage is declared above and used by useRealTimeMessages hook

  // Message actions (reactions, editing, deleting) handled by useMessageActions hook

  // Haptic feedback simulation for mobile
  const triggerHapticFeedback = useCallback(() => { hapticMedium(); }, []);

  const dismissContextMenu = useCallback(() => {
    setLongPressedMessage(null);
    setMessagePosition(null);
    setShowReactionPicker(null);
    dismissCooldownRef.current = true;
    setTimeout(() => { dismissCooldownRef.current = false; }, 600);
  }, []);

  // Enhanced long press with haptic feedback
  const handleLongPressStart = useCallback((messageId: string, event: React.TouchEvent | React.MouseEvent) => {
    if (dismissCooldownRef.current) return;

    const target = event.currentTarget as HTMLElement;
    const bubbleEl = target.querySelector('.message-bubble') as HTMLElement | null;
    const rectEl = bubbleEl || target;

    const timer = setTimeout(() => {
      if (dismissCooldownRef.current) return;
      const rect = rectEl.getBoundingClientRect();
      setMessagePosition({ x: rect.left, y: rect.top, width: rect.width, height: rect.height });

      if (window.getSelection) {
        window.getSelection()?.removeAllRanges();
        document.getSelection()?.removeAllRanges();
      }

      setShowReactionPicker(messageId);
      setLongPressedMessage(messageId);
      triggerHapticFeedback();

      // Swallow the release of this opening press so the menu holds open.
      openCooldownRef.current = true;
      setTimeout(() => { openCooldownRef.current = false; }, 600);
    }, 500);

    setLongPressTimer(timer);
  }, [triggerHapticFeedback]);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    // Clear any text selection that might have occurred
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        selection.removeAllRanges();
      }
    }
    // Don't clear longPressedMessage here - let it be cleared when actions are clicked
  }, [longPressTimer]);

  // Clear context menu and position when clicking outside
  const handleClickOutside = (event: MouseEvent) => {
    // Ignore the release of the gesture that just opened the menu.
    if (openCooldownRef.current) return;
    const target = event.target as HTMLElement;
    if (!target.closest('[data-message-id]') && !target.closest('.context-menu')) {
      dismissContextMenu();
      setTappedMessageForTimestamp(null);
    }
  };

  // Handle tap - toggle timestamp visibility (like iMessage)
  const handleMessageTap = useCallback((messageId: string) => {
    setTappedMessageForTimestamp(prev => prev === messageId ? null : messageId);
  }, []);

  // Message editing, deleting, and replying handled by useMessageActions hook
  // Key press handling handled by UnifiedMessageInput component

  const handleEditGroupName = () => {
    setEditingGroupName(true);
    setGroupNameInput(conversation?.title || 'Group Chat');
  };

  const handleSaveGroupName = async () => {
    if (!conversation || !groupNameInput.trim()) return;
    
    try {
      const response = await UnifiedMessagingService.getInstance().updateConversation(conversation.id, {
        title: groupNameInput.trim()
      });
      
      if (response.success) {
        // Update local conversation state
        queryClient.setQueryData(['conversation', currentConversationId], (prev: UnifiedConversation | undefined) =>
          prev ? { ...prev, title: groupNameInput.trim() } : prev
        );
        setEditingGroupName(false);
        setShowGroupInfo(false);
      } else {
        console.error('❌ Failed to update group name:', response.error);
        // You could show a toast error here
      }
    } catch (error) {
      console.error('❌ Failed to update group name:', error);
      // You could show a toast error here
    }
  };

  const handleCancelGroupNameEdit = () => {
    setEditingGroupName(false);
    setGroupNameInput(conversation?.title || 'Group Chat');
  };

  const handleRemoveParticipant = async (participantId: string) => {
    if (!conversation) return;

    try {
      const response = await UnifiedMessagingService.getInstance().removeParticipant(conversation.id, participantId);

      if (response.success) {
        // Refresh conversation data to get updated participant list
        await loadConversation();
      } else {
        console.error('❌ Failed to remove participant:', response.error);
        // You could show a toast error here
      }
    } catch (error) {
      console.error('❌ Failed to remove participant:', error);
      // You could show a toast error here
    }
  };

  const handleSearchUsers = async (query: string, loadAll: boolean = false) => {
    // Allow empty query when loadAll is true (for loading friends list)
    if (!loadAll && query.trim().length > 0 && query.length < 2) {
      // Don't search yet, but keep existing results (friends)
      return;
    }

    try {
      setSearchingParticipants(true);
      // Pass empty string to get all friends, or the query to filter
      const response = await UnifiedMessagingService.getInstance().searchUsers(query.trim(), 50);

      if (response.success) {
        // Filter out users who are already participants
        const existingParticipantIds = conversation?.participants?.map(p => p.id) || [];
        const availableUsers = response.data.filter(user => !existingParticipantIds.includes(user.id));
        setParticipantSearchResults(availableUsers);
      } else {
        console.error('❌ Failed to search users:', response.error);
        setParticipantSearchResults([]);
      }
    } catch (error) {
      console.error('❌ Error searching users:', error);
      setParticipantSearchResults([]);
    } finally {
      setSearchingParticipants(false);
    }
  };

  const handleAddParticipants = async () => {
    if (!conversation || selectedUsers.length === 0) return;
    
    try {
      const response = await UnifiedMessagingService.getInstance().addParticipants(conversation.id, selectedUsers);

      if (response.success) {
        // Refresh conversation data to get updated participant list
        await loadConversation();
        // Reset modal state
        setShowAddParticipants(false);
        setParticipantSearchQuery('');
        setParticipantSearchResults([]);
        setSelectedUsers([]);
      } else {
        console.error('❌ Failed to add participants:', response.error);
        // You could show a toast error here
      }
    } catch (error) {
      console.error('❌ Failed to add participants:', error);
      // You could show a toast error here
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Sync React Query data into local state
  useEffect(() => {
    if (_messagesData) {
      setMessages(_messagesData);
      setHasMoreMessages(_messagesData.length === MESSAGES_PER_PAGE);
      setPage(1);
    }
  }, [_messagesData]);

  useEffect(() => {
    if (_conversationData) {
      setConversation(_conversationData);
      setConversationType((_conversationData.type || 'direct') as 'channel' | 'direct');
      service.markConversationAsRead(currentConversationId!).then(() => refreshUnreadCount());
    }
  }, [_conversationData?.id]);

  // Clear messages when switching to a different conversation (before new data arrives)
  useEffect(() => {
    if (!_messagesData) {
      setMessages([]);
      setPage(1);
      setHasMoreMessages(true);
    }
  }, [currentConversationId]);

  // Handle participant search - load friends on modal open, filter as user types
  useEffect(() => {
    if (!showAddParticipants) return;

    // Load all friends immediately when modal opens
    if (participantSearchQuery === '') {
      handleSearchUsers('', true); // loadAll = true
      return;
    }

    // Debounce search when user is typing
    const timeoutId = setTimeout(() => {
      handleSearchUsers(participantSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [participantSearchQuery, showAddParticipants]);

  // Removed 5-second polling - we use real-time subscriptions instead
  // The polling was causing duplicate messages because it would refetch
  // messages while they were being sent, conflicting with optimistic updates

  // Real-time subscriptions handled by useRealTimeMessages hook

  // Online/offline detection (retry logic handled by useOfflineMessages hook)
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };

    // Set initial state
    setIsOnline(navigator.onLine);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  }, [longPressTimer]);

  // Click outside to close reaction picker
  // Use 'click' instead of 'mousedown' so button onClick handlers fire first
  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showReactionPicker]);

  // Helper functions
  const getConversationTitle = () => {
    if (!conversation) return t.messaging.loading;
    
    if (conversation.type === 'channel') {
      return `#${conversation.title || conversation.display_name || 'Unknown Channel'}`;
    } else if (conversation.type === 'group') {
      // For group chats, show the group title or "Group Chat"
      return conversation.title || conversation.display_name || t.messaging.groupChatTitle;
    } else {
      // For direct messages, show the other person's name
      const participants = conversation.participants || [];
      const otherParticipant = participants.find(p => p.id !== user?.id);
      return otherParticipant?.display_name || 'Direct Message';
    }
  };

  const getConversationAvatar = () => {
    if (!conversation) return null;
    
    if (conversation.type === 'channel') {
      return conversation.avatar_url;
    } else {
      const participants = conversation.participants || [];
      const otherParticipant = participants.find(p => p.id !== user?.id);
      return otherParticipant?.avatar_url;
    }
  };

  const getConversationInitials = () => {
    if (!conversation) return '?';
    
    if (conversation.type === 'channel') {
      return (conversation.title || conversation.display_name || 'C').charAt(0).toUpperCase();
    } else if (conversation.type === 'group') {
      // For group chats, show the group title initial or "G"
      return (conversation.title || conversation.display_name || 'G').charAt(0).toUpperCase();
    } else {
      const participants = conversation.participants || [];
      const otherParticipant = participants.find(p => p.id !== user?.id);
      return (otherParticipant?.display_name || 'D').charAt(0).toUpperCase();
    }
  };

  // While channels are fetching (club mode, first render), show the skeleton
  if (clubId && channelData.channelsLoading && !isChannelMode) {
    return (
      <div className={`flex flex-col ${embedded ? 'h-full' : 'h-screen'}`}
        style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 rounded-full border-2 border-slate-200 border-t-[#2A4494] animate-spin" />
            <p className="text-sm text-slate-400">{t.clubCommunity?.loading ?? 'Loading channels…'}</p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading && messages.length === 0) {
    return (
      <div
        className={`flex flex-col ${embedded ? 'h-full' : 'h-screen'}`}
        style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}
      >
        <style dangerouslySetInnerHTML={{ __html: `
          @keyframes chat-shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          .chat-skel {
            background: linear-gradient(90deg, #E2E8F0 25%, #F1F5F9 50%, #E2E8F0 75%);
            background-size: 200% 100%;
            animation: chat-shimmer 1.5s infinite;
          }
        `}} />

        {/* Header skeleton */}
        <div
          style={{
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            paddingTop: 'max(12px, env(safe-area-inset-top))'
          }}
        >
          {/* Back chevron */}
          <div className="chat-skel" style={{ width: 36, height: 36, borderRadius: 12, flexShrink: 0 }} />
          {/* Avatar */}
          <div className="chat-skel" style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0 }} />
          {/* Name + status */}
          <div style={{ flex: 1 }}>
            <div className="chat-skel" style={{ height: 14, borderRadius: 7, width: '45%', marginBottom: 6 }} />
            <div className="chat-skel" style={{ height: 11, borderRadius: 6, width: '25%' }} />
          </div>
          {/* More button */}
          <div className="chat-skel" style={{ width: 32, height: 32, borderRadius: 10, flexShrink: 0 }} />
        </div>

        {/* Messages skeleton */}
        <div style={{ flex: 1, padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'hidden' }}>
          {/* Incoming */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <div className="chat-skel" style={{ width: 32, height: 32, borderRadius: 10, flexShrink: 0 }} />
            <div className="chat-skel" style={{ height: 44, borderRadius: '18px 18px 18px 4px', width: '62%' }} />
          </div>
          {/* Outgoing */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div className="chat-skel" style={{ height: 36, borderRadius: '18px 18px 4px 18px', width: '48%', animationDelay: '0.1s' }} />
          </div>
          {/* Incoming tall */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <div className="chat-skel" style={{ width: 32, height: 32, borderRadius: 10, flexShrink: 0 }} />
            <div className="chat-skel" style={{ height: 64, borderRadius: '18px 18px 18px 4px', width: '72%', animationDelay: '0.05s' }} />
          </div>
          {/* Outgoing */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div className="chat-skel" style={{ height: 44, borderRadius: '18px 18px 4px 18px', width: '55%', animationDelay: '0.15s' }} />
          </div>
          {/* Incoming short */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <div className="chat-skel" style={{ width: 32, height: 32, borderRadius: 10, flexShrink: 0 }} />
            <div className="chat-skel" style={{ height: 36, borderRadius: '18px 18px 18px 4px', width: '38%', animationDelay: '0.08s' }} />
          </div>
          {/* Outgoing tall */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div className="chat-skel" style={{ height: 56, borderRadius: '18px 18px 4px 18px', width: '65%', animationDelay: '0.12s' }} />
          </div>
        </div>

        {/* Input bar skeleton */}
        <div
          style={{
            background: 'rgba(255,255,255,0.95)',
            borderTop: '1px solid rgba(0,0,0,0.06)',
            padding: '12px 16px',
            paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}
        >
          <div className="chat-skel" style={{ flex: 1, height: 44, borderRadius: 22 }} />
          <div className="chat-skel" style={{ width: 44, height: 44, borderRadius: 22, flexShrink: 0 }} />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`${embedded ? 'h-full' : 'min-h-screen'} flex items-center justify-center bg-gray-50`}>
        <div className="text-center">
          <div className="text-lg font-semibold mb-2 text-red-600">{t.common?.errorTitle || "Error"}</div>
          <p className="text-sm text-gray-500">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col ${embedded ? 'h-full' : 'h-screen'}`}
      style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        ...(embedded ? {} : { overflow: 'hidden' }),
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes chat-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .chat-skel {
          background: linear-gradient(90deg, #E2E8F0 25%, #F1F5F9 50%, #E2E8F0 75%);
          background-size: 200% 100%;
          animation: chat-shimmer 1.5s infinite;
        }
      `}} />
      {/* Channel sidebar */}
      {isChannelMode && showChannelSidebar && (
        <>
          {/* Backdrop — full screen dim including above the header */}
          <div
            className="fixed inset-0 z-40"
            style={{
              background: 'rgba(15,23,42,0.30)',
            }}
            onClick={() => setShowChannelSidebar(false)}
          />

          {/* Panel — left-anchored floating card (matches ClubChatMinimal design) */}
          <div
            className="fixed z-50 flex flex-col bg-white border border-slate-200"
            style={{
              top: 'calc(env(safe-area-inset-top, 0px) + 56px)',
              bottom: 'calc(60px + env(safe-area-inset-bottom, 0px) + 8px)',
              left: 12,
              width: 272,
              borderRadius: 20,
              boxShadow: '0 8px 40px rgba(15,23,42,0.18), 0 2px 8px rgba(15,23,42,0.10)',
            }}
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-slate-100 flex-shrink-0 flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  {t.clubCommunity?.community ?? 'Community'}
                </p>
                <h3 className="text-[17px] font-bold text-slate-900 mt-1">
                  {t.clubCommunity?.channels ?? 'Channels'}
                </h3>
              </div>
              <button
                onClick={() => setShowChannelSidebar(false)}
                className="w-8 h-8 bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0 transition-colors mt-1"
                style={{ borderRadius: 8 }}
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>

            {/* Channel list */}
            <div className="flex-1 overflow-y-auto bg-slate-50" style={{ borderRadius: '0 0 20px 20px' }}>
              <div className="px-3 pt-3 pb-4 space-y-1">
                {channelData.channels.map((channel) => {
                  const Icon = CHANNEL_ICONS[channel.channel_type] ?? Hash;
                  const isActive = channelData.activeChannel === channel.id;
                  const channelDescMap: Record<string, string> = {
                    general: t.clubCommunity?.channelDescriptions?.general ?? '',
                    'course-tips': t.clubCommunity?.channelDescriptions?.courseTips ?? '',
                    announcements: t.clubCommunity?.channelDescriptions?.announcements ?? '',
                    'playing-today': t.clubCommunity?.channelDescriptions?.playingToday ?? '',
                    tournaments: t.clubCommunity?.channelDescriptions?.tournaments ?? '',
                    equipment: t.clubCommunity?.channelDescriptions?.equipment ?? '',
                    'club-events': t.clubCommunity?.channelDescriptions?.clubEvents ?? '',
                    'round-shares': t.clubCommunity?.channelDescriptions?.roundShares ?? '',
                  };
                  const description = channelDescMap[channel.name] || channel.description || channel.name;

                  return (
                    <button
                      key={channel.id}
                      onClick={() => {
                        channelData.setActiveChannel(channel.id);
                        onChannelSwitch?.(channel.id);
                        setShowChannelSidebar(false);
                      }}
                      className="w-full text-left flex items-center gap-3 px-3 py-3 transition-all min-h-[48px]"
                      style={isActive
                        ? { borderRadius: 12, background: 'linear-gradient(135deg, #52B788 0%, #2D5A3D 50%, #1B4332 100%)', boxShadow: '0 2px 8px rgba(27,67,50,0.22)' }
                        : { borderRadius: 12, background: 'transparent' }
                      }
                    >
                      <div
                        className="w-9 h-9 flex items-center justify-center flex-shrink-0"
                        style={{
                          borderRadius: 10,
                          background: isActive ? 'rgba(255,255,255,0.18)' : '#1B433214',
                          border: isActive ? 'none' : '1px solid #1B433228',
                        }}
                      >
                        <Icon className="w-4 h-4" style={{ color: isActive ? '#fff' : '#1B4332' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold truncate" style={{ color: isActive ? '#fff' : '#0F172A' }}>
                          {channel.name}
                        </div>
                        <div className="text-[11px] truncate mt-0.5" style={{ color: isActive ? 'rgba(255,255,255,0.65)' : '#94A3B8' }}>
                          {description}
                        </div>
                      </div>
                      {(channel.unread_count ?? 0) > 0 && !isActive && (
                        <span style={{
                          background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)',
                          color: 'white',
                          fontSize: 10,
                          fontWeight: 700,
                          borderRadius: 99,
                          minWidth: 20,
                          height: 20,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0 6px',
                          flexShrink: 0,
                          boxShadow: '0 1px 4px rgba(42,68,148,0.30)',
                        }}>
                          {(channel.unread_count ?? 0) > 99 ? '99+' : channel.unread_count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Header */}
      {!hideHeader && (isChannelMode ? (
        /* Channel header — flex-shrink-0 stays pinned inside the flex column */
        <div
          className="flex-shrink-0 px-3 pb-2"
          style={{
            zIndex: 40,
            background: 'rgba(255,255,255,0.82)',
            backdropFilter: 'blur(20px) saturate(180%)',
            borderBottom: '1px solid rgba(15,23,42,0.08)',
            boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 8px rgba(15,23,42,0.06)',
            paddingTop: 'max(8px, env(safe-area-inset-top))',
          }}
        >
          <div className="flex items-center gap-2 max-w-sm mx-auto">
            {/* Back button — glass chip */}
            <button
              onClick={() => handleBack()}
              className="flex items-center justify-center flex-shrink-0 transition-all hover:scale-105 active:scale-95"
              style={{
                width: 36, height: 36, borderRadius: 12,
                background: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(20px) saturate(180%)',
                border: '1px solid rgba(15,23,42,0.08)',
                boxShadow: '0 0.9px 0 rgba(255,255,255,0.9) inset, 0 2px 6px rgba(15,23,42,0.06)',
              }}
            >
              <ChevronLeft className="w-5 h-5 text-[#1B4332]" />
            </button>

            {/* Channel name pill — tapping opens sidebar */}
            <button
              onClick={() => setShowChannelSidebar(s => !s)}
              className="flex-1 flex items-center justify-center min-w-0"
            >
              <div
                className="flex items-center gap-1.5 px-3 py-1.5"
                style={{
                  borderRadius: 20,
                  background: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  border: '1px solid rgba(15,23,42,0.08)',
                  boxShadow: '0 0.9px 0 rgba(255,255,255,0.9) inset, 0 2px 6px rgba(15,23,42,0.06)',
                }}
              >
                <Hash className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#1B4332' }} />
                <span className="text-[14px] font-bold text-slate-900 truncate max-w-[120px]">
                  {channelData.channels.find(c => c.id === channelData.activeChannel)?.name ?? (t.clubCommunity?.loading ?? '…')}
                </span>
                <ChevronLeft className="w-3.5 h-3.5 flex-shrink-0 -rotate-90" style={{ color: '#1B4332' }} />
              </div>
            </button>

            {/* Search button */}
            <button
              onClick={() => setShowChannelSearch(s => !s)}
              className="flex items-center justify-center flex-shrink-0 transition-all hover:scale-105 active:scale-95"
              style={{
                width: 36, height: 36, borderRadius: 12,
                background: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(20px) saturate(180%)',
                border: '1px solid rgba(15,23,42,0.08)',
                boxShadow: '0 0.9px 0 rgba(255,255,255,0.9) inset, 0 2px 6px rgba(15,23,42,0.06)',
              }}
            >
              <Search className="w-4 h-4 text-[#1B4332]" />
            </button>

            {/* Members button + dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowMembersDropdown(s => !s)}
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-all hover:scale-105"
                style={{
                  background: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  border: '1px solid rgba(15,23,42,0.08)',
                  boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 6px rgba(15,23,42,0.06)',
                }}
              >
                <Users2 className="w-5 h-5 text-[#1B4332]" />
              </button>

              {showMembersDropdown ? (
                <div
                  className="absolute right-0 top-12 w-56 rounded-2xl z-50 overflow-hidden"
                  style={{
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(20px) saturate(180%)',
                    border: '1px solid rgba(15,23,42,0.08)',
                    boxShadow: '0 4px 24px rgba(15,23,42,0.12), 0 1px 0 rgba(255,255,255,0.8) inset',
                  }}
                >
                  <div className="p-3" style={{ borderBottom: '1px solid rgba(15,23,42,0.08)' }}>
                    <div className="font-bold text-slate-900">{t.clubCommunity?.members ?? 'Members'}</div>
                    <div className="text-xs text-gray-500">
                      {channelData.clubMembers.length}{' '}
                      {channelData.clubMembers.length === 1
                        ? (t.clubCommunity?.member ?? 'member')
                        : (t.clubCommunity?.members_plural ?? 'members')}
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {channelData.clubMembers.map((member: any) => (
                      <button
                        key={member.id}
                        onClick={() => { setShowMembersDropdown(false); navigate(`/profile/${member.id}`); }}
                        className="w-full px-3 py-2 hover:bg-gray-50 flex items-center space-x-2 text-left"
                      >
                        {member.avatar_url ? (
                          <img src={member.avatar_url} alt={member.display_name || 'User'} className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <div className="w-6 h-6 bg-[#1B4332] rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-medium">{member.display_name?.[0] || 'U'}</span>
                          </div>
                        )}
                        <span className="text-sm text-gray-900">{member.display_name ?? (t.clubCommunity?.unknownUser ?? 'Unknown')}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Channel search bar (shown when search toggled) */}
          {showChannelSearch ? (
            <div className="max-w-sm mx-auto mt-2">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t.clubCommunity?.searchMessages ?? 'Search messages…'}
                className="w-full px-4 py-2 rounded-full text-sm focus:outline-none transition-all"
                style={{
                  background: 'rgba(255,255,255,0.9)',
                  border: '1px solid rgba(15,23,42,0.10)',
                  boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset',
                }}
                autoFocus
              />
            </div>
          ) : null}
        </div>
      ) : (
        <ConversationHeader
          conversation={conversation}
          conversationType={conversationType}
          isOnline={isOnline}
          offlineMessagesCount={offlineMessages.length}
          isRetrying={isRetrying}
          showSearch={showSearch}
          onBack={() => handleBack()}
          onShowGroupInfo={() => setShowGroupInfo(!showGroupInfo)}
          onShowMoreMenu={() => setShowMoreMenu(!showMoreMenu)}
          onToggleSearch={() => setShowSearch(!showSearch)}
          showMoreMenu={showMoreMenu}
          isMuted={isMuted}
          onMuteNotifications={handleMuteNotifications}
          onClearChat={showClearChatConfirmation}
          onBlockOrLeave={showBlockConfirmation}
          language={language}
          currentUserId={user?.id}
          overrideName={overrideName}
          embedded={embedded}
        />
      ))}

      {/* Old header code - replaced by ConversationHeader component */}
      {false && <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-between safe-area-top shadow-sm">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => handleBack()}
            className="p-2 rounded-full hover:bg-gray-100 transition-all duration-150 hover:scale-110 active:scale-95"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          
          <div className="flex items-center space-x-3">
            <div 
              className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center text-white text-sm font-bold shadow-lg border-2 border-white cursor-pointer hover:scale-105 transition-all duration-200"
              style={{
                background: getConversationAvatar() 
                  ? `url(${getConversationAvatar()}) center/cover` 
                  : 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)'
              }}
              onClick={() => {
                if (conversationType === 'direct' && conversation?.participants) {
                  const otherParticipant = conversation.participants.find(p => p.id !== user?.id);
                  if (otherParticipant) {
                    navigate(`/profile/${otherParticipant.id}`);
                  }
                } else if (conversationType === 'channel' && conversation?.club_id) {
                  navigate(`/club/${conversation.club_id}`);
                }
              }}
            >
              {!getConversationAvatar() && getConversationInitials()}
            </div>
            
            <div 
              className="cursor-pointer hover:bg-gray-50 rounded-lg px-2 py-1 -ml-2 transition-all duration-200"
              onClick={() => {
                if (conversationType === 'direct' && conversation?.participants) {
                  const otherParticipant = conversation.participants.find(p => p.id !== user?.id);
                  if (otherParticipant) {
                    navigate(`/profile/${otherParticipant.id}`);
                  }
                } else if (conversationType === 'channel' && conversation?.club_id) {
                  navigate(`/club/${conversation.club_id}`);
                }
              }}
            >
              <h2 className="font-semibold text-gray-900 text-lg hover:text-[#2A4494] transition-colors duration-200">{getConversationTitle()}</h2>
              <p className="text-sm text-gray-500 flex items-center">
                <span className={`w-2 h-2 rounded-full mr-2 ${isOnline ? 'bg-blue-500' : 'bg-gray-400'}`} />
                {conversationType === 'channel' ? (
                  <>
                    {t.messaging.membersCount.replace('{count}', (conversation?.participants?.length || 0).toString())}
                    {!isOnline && ' • Offline'}
                  </>
                ) : conversation?.type === 'group' ? (
                  <>
                    {t.messaging.participantsCount.replace('{count}', (conversation?.participants?.length || 0).toString())}
                    {conversation?.last_message_at ? <>
                      {' • '}
                      {formatDateWithLocale(new Date(conversation.last_message_at))}
                                                     </> : null}
                  </>
                ) : (
                  <>
                    {isOnline ? 'Online' : 'Offline'}
                    {conversation?.last_message_at ? <>
                      {' • '}
                      {formatDateWithLocale(new Date(conversation.last_message_at))}
                                                     </> : null}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {!isOnline && (
            <span className="text-xs text-red-500 bg-red-100 px-3 py-1 rounded-full font-medium">
              Offline
            </span>
          )}
          {offlineMessages.length > 0 && (
            <span className="text-xs text-orange-600 bg-orange-100 px-3 py-1 rounded-full font-medium">
              {offlineMessages.length} queued
            </span>
          )}
          {isRetrying ? <span className="text-xs text-blue-600 bg-blue-100 px-3 py-1 rounded-full font-medium">
            Sending...
          </span> : null}
          
          {/* Search button */}
          <button 
            onClick={() => setShowSearch(!showSearch)}
            className="p-2 rounded-full hover:bg-gray-100 transition-all duration-150 hover:scale-110 active:scale-95"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
          
          {/* Group Info Button for Group Chats */}
          {conversation?.type === 'group' && (
            <button
              onClick={() => setShowGroupInfo(!showGroupInfo)}
              className="p-2 rounded-full hover:bg-gray-100 transition-all duration-150 hover:scale-110 active:scale-95"
            >
              <Users className="h-5 w-5" />
            </button>
          )}
          
          {/* More menu button */}
          <div className="relative">
            <button 
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className="p-2 rounded-full hover:bg-gray-100 transition-all duration-150 hover:scale-110 active:scale-95"
              data-more-menu
            >
              <MoreVertical className="h-5 w-5" />
            </button>
            
            {/* More menu dropdown */}
            {showMoreMenu ? <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50 animate-slide-up"
              data-more-menu
                            >
              <button
                onClick={handleMuteNotifications}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-150"
              >
                <span className="text-gray-900 font-medium">
                  {isMuted ? t.messaging.unmuteNotifications : t.messaging.muteNotifications}
                </span>
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
              </button>
                
              <button
                onClick={showClearChatConfirmation}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-150"
              >
                <span className="text-gray-900 font-medium">{t.messaging.clearChat}</span>
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
                
              <div className="border-t border-gray-200 my-1" />
                
              <button
                onClick={showBlockConfirmation}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-red-50 transition-colors duration-150"
              >
                <span className="text-red-600 font-medium">
                  {conversationType === 'direct' ? t.messaging.blockUser : t.messaging.leaveChannel}
                </span>
                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                </svg>
              </button>
                            </div> : null}
          </div>
        </div>
      </div>}

      {/* Search input */}
      {showSearch ? <div className="px-4 py-3 animate-slide-down" style={{ background: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(20px) saturate(180%)', borderBottom: '1px solid rgba(15,23,42,0.08)', boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 6px rgba(15,23,42,0.06)' }}>
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder={t.messaging.searchInConversation}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 transition-all duration-200"
              style={{ background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(15,23,42,0.10)', boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset' }}
              autoFocus
            />
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button
            onClick={() => {
                setShowSearch(false);
                setSearchQuery('');
              }}
            className="p-2 rounded-full hover:bg-gray-100 transition-all duration-150"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {searchQuery ? <div className="mt-2 text-xs text-gray-500">
          {filteredMessages.length} result{filteredMessages.length !== 1 ? 's' : ''} found
        </div> : null}
                    </div> : null}

      {/* Group Info Modal */}
      <GroupInfoModal
        isOpen={showGroupInfo && conversation?.type === 'group'}
        conversation={conversation}
        currentUserId={user?.id}
        editingGroupName={editingGroupName}
        groupNameInput={groupNameInput}
        onClose={() => setShowGroupInfo(false)}
        onEditGroupName={handleEditGroupName}
        onCancelGroupNameEdit={handleCancelGroupNameEdit}
        onSaveGroupName={handleSaveGroupName}
        onGroupNameInputChange={setGroupNameInput}
        onRemoveParticipant={handleRemoveParticipant}
        showAddParticipants={showAddParticipants}
        onShowAddParticipants={() => {
          setShowAddParticipants(true);
          setParticipantSearchQuery('');
          setParticipantSearchResults([]);
          setSelectedUsers([]);
        }}
        onCloseAddParticipants={() => setShowAddParticipants(false)}
        participantSearchQuery={participantSearchQuery}
        onParticipantSearchQueryChange={setParticipantSearchQuery}
        participantSearchResults={participantSearchResults}
        searchingParticipants={searchingParticipants}
        selectedUsers={selectedUsers}
        onToggleUserSelection={toggleUserSelection}
        onHandleAddParticipants={handleAddParticipants}
      />
      
      {/* Old Group Info Panel - replaced by GroupInfoModal component */}
      {false && showGroupInfo && conversation?.type === 'group' ? <div className="bg-white border-b border-gray-200 px-4 py-4 animate-slide-down">
        <div className="space-y-4">
          {/* Group Title and Edit */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">{t.messaging.groupInfo}</h3>
            <button
              onClick={() => setShowGroupInfo(false)}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
            
          {/* Group Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{t.messaging.groupName}</label>
            {editingGroupName ? (
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={groupNameInput}
                  onChange={(e) => setGroupNameInput(e.target.value)}
                  onKeyPress={(e) => {
                      if (e.key === 'Enter') handleSaveGroupName();
                      if (e.key === 'Escape') handleCancelGroupNameEdit();
                    }}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1B4332] focus:border-transparent"
                  placeholder={t.messaging.enterGroupName}
                  autoFocus
                />
                <button 
                  onClick={handleSaveGroupName}
                  className="px-3 py-2 bg-[#1B4332] text-white text-sm rounded-lg hover:bg-[#2D5A3D] transition-colors"
                >
                  Save
                </button>
                <button 
                  onClick={handleCancelGroupNameEdit}
                  className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
              ) : (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-900">
                    {conversation.title || 'Group Chat'}
                  </span>
                  <button 
                    onClick={handleEditGroupName}
                    className="px-3 py-1 text-[#1B4332] text-sm hover:bg-[#1B4332]/10 rounded-lg transition-colors"
                  >
                    {t.messaging.edit}
                  </button>
                </div>
              )}
          </div>
            
          {/* Participants */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                {t.messaging.participantsLabel.replace('{count}', (conversation.participants?.length || 0).toString())}
              </label>
              {conversation.created_by === user?.id && (
              <button
                onClick={() => {
                      setShowAddParticipants(true);
                      setParticipantSearchQuery('');
                      setParticipantSearchResults([]);
                      setSelectedUsers([]);
                    }}
                className="px-3 py-1 text-[#1B4332] text-sm hover:bg-[#1B4332]/10 rounded-lg transition-colors"
              >
                + {t.messaging.add}
              </button>
                )}
            </div>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {conversation.participants?.map((participant) => (
                <div key={participant.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={participant.avatar_url || undefined} />
                      <AvatarFallback className="bg-[#1B4332] text-white text-xs font-medium">
                        {participant.display_name?.[0] || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium text-gray-900">
                      {participant.display_name || 'Unknown User'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {conversation.created_by === participant.id && (
                    <span className="text-xs text-[#1B4332] bg-[#1B4332]/10 px-2 py-1 rounded-full">
                      Admin
                        </span>
                      )}
                    {conversation.created_by === user?.id && participant.id !== user?.id && (
                    <button
                      onClick={() => handleRemoveParticipant(participant.id)}
                      className="text-red-500 hover:text-red-700 text-xs px-2 py-1 hover:bg-red-50 rounded transition-colors"
                    >
                      {t.messaging.remove}
                    </button>
                      )}
                  </div>
                </div>
                ))}
            </div>
          </div>
        </div>
                                                         </div> : null}

      {/* Old Add Participants Modal - now handled by GroupInfoModal */}
      {false && showAddParticipants ? <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{t.messaging.addParticipants}</h3>
              <button
                onClick={() => {
                    setShowAddParticipants(false);
                    setParticipantSearchQuery('');
                    setParticipantSearchResults([]);
                    setSelectedUsers([]);
                  }}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
            
          <div className="p-4">
            <p className="text-sm text-gray-600 mb-4">
              Search for users to add to your group chat.
            </p>
              
            <div className="space-y-3">
              <input
                type="text"
                placeholder={t.messaging.searchUsersInChat}
                value={participantSearchQuery}
                onChange={(e) => setParticipantSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1B4332] focus:border-transparent"
              />
                
              {/* Search Results */}
              {searchingParticipants ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1B4332] mx-auto" />
                  <p className="text-sm text-gray-500 mt-2">Searching...</p>
                </div>
                ) : participantSearchResults.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {participantSearchResults.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-[#1B4332] rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-medium">
                              {user.display_name?.[0] || 'U'}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {user.display_name || 'Unknown User'}
                          </span>
                        </div>
                        <button
                          onClick={() => toggleUserSelection(user.id)}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            selectedUsers.includes(user.id)
                              ? 'bg-[#1B4332] text-white'
                              : 'bg-white text-[#1B4332] border border-[#1B4332] hover:bg-[#1B4332] hover:text-white'
                          }`}
                        >
                          {selectedUsers.includes(user.id) ? 'Selected' : 'Select'}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : participantSearchQuery.length >= 2 ? (
                  <div className="text-center py-4">
                    <p className="text-gray-500">No users found</p>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-400">Type at least 2 characters to search</p>
                  </div>
                )}

              {/* Add Button */}
              {selectedUsers.length > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={handleAddParticipants}
                  className="w-full bg-[#1B4332] text-white py-2 px-4 rounded-lg hover:bg-[#1B4332]/90 transition-colors"
                >
                  {t.messaging.addParticipantCount.replace('{count}', selectedUsers.length.toString()).replace('{plural}', selectedUsers.length !== 1 ? 's' : '')}
                </button>
              </div>
                )}
            </div>
          </div>
        </div>
                             </div> : null}

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 pb-6 space-y-4"
        style={{ overscrollBehavior: 'contain' }}
        onScroll={isChannelMode ? handleChannelScroll : handleScrollForButton}
      >
        {isChannelMode && channelData.isFetchingNextPage && (
          <div style={{ textAlign: 'center', padding: '8px 0', color: '#9ca3af', fontSize: 12 }}>
            Loading older messages…
          </div>
        )}
        {activeMessages.length === 0 && (isChannelMode ? channelData.messagesLoading : loading) ? (
          // Still fetching — render message-shaped skeletons so no flash of empty state
          <div className="space-y-4 px-1 pt-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className={`flex items-end gap-2 ${i % 3 === 2 ? 'flex-row-reverse' : ''}`}>
                <div className="chat-skel rounded-full flex-shrink-0" style={{ width: 28, height: 28 }} />
                <div className="chat-skel rounded-2xl" style={{ width: `${40 + (i * 13) % 40}%`, height: 36 }} />
              </div>
            ))}
          </div>
        ) : activeMessages.length === 0 && !isChannelMode ? (
          <div className="flex items-center justify-center h-full py-8">
            <EmptyState
              icon={MessageCircle}
              title={t.messaging.noMessagesYetInConversation}
              description={t.messaging.startConversation}
              domain="people"
              stateType="first-run"
              className="max-w-xs"
            />
          </div>
        ) : (
          <div className="relative">
            {useVirtualScroll && visibleRange && (
              <div style={{ height: visibleRange.startIndex * ESTIMATED_ROW_HEIGHT }} />
            )}
            {(useVirtualScroll && visibleRange
              ? filteredMessages.slice(visibleRange.startIndex, visibleRange.endIndex + 1)
              : filteredMessages
            ).map((message, sliceIndex) => {
              const index = useVirtualScroll && visibleRange
                ? visibleRange.startIndex + sliceIndex
                : sliceIndex;
              const isOwnMessage = rightSideSenderId != null && message.sender_id === rightSideSenderId;
              const isEditing = editingMessage === message.id;
              const prevMsg = filteredMessages[index - 1];
              const nextMsg = filteredMessages[index + 1];
              const isFirstInGroup = isChannelMode
                ? (index === 0 || !sameGroup(prevMsg!, message))
                : (index === 0 || prevMsg?.sender_id !== message.sender_id);
              const isLastInGroup = isChannelMode
                ? (index === filteredMessages.length - 1 || !sameGroup(message, nextMsg!))
                : (index === filteredMessages.length - 1 || nextMsg?.sender_id !== message.sender_id);
              const isMiddleInGroup = !isFirstInGroup && !isLastInGroup;

              // Date separator logic
              const msgDate = new Date(message.created_at);
              const prevDate = index > 0 ? new Date(filteredMessages[index - 1].created_at) : null;
              const isDifferentDay = !prevDate || msgDate.toDateString() !== prevDate.toDateString();
              let dateSeparatorLabel: string | null = null;
              if (isDifferentDay) {
                const today = new Date();
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                if (msgDate.toDateString() === today.toDateString()) {
                  dateSeparatorLabel = t.messaging.today;
                } else if (msgDate.toDateString() === yesterday.toDateString()) {
                  dateSeparatorLabel = t.messaging.yesterday;
                } else {
                  dateSeparatorLabel = msgDate.toLocaleDateString(
                    language === 'fr' ? 'fr-FR' : language === 'nl' ? 'nl-NL' : 'en-GB',
                    { day: 'numeric', month: 'long', year: msgDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined }
                  );
                }
              }

              return (
                <React.Fragment key={message.id}>
                  {dateSeparatorLabel && (
                    <div className="flex items-center gap-3 my-3">
                      <div className="flex-1 h-px" style={{ background: 'rgba(0,0,0,0.08)' }} />
                      <span className="text-xs font-medium px-2" style={{ color: 'rgba(0,0,0,0.35)' }}>
                        {dateSeparatorLabel}
                      </span>
                      <div className="flex-1 h-px" style={{ background: 'rgba(0,0,0,0.08)' }} />
                    </div>
                  )}
                {(() => {
                  const isAnnouncement = isChannelMode &&
                    !!((message.message_data as any)?.isAnnouncement);
                  const announcementId = isAnnouncement
                    ? ((message.message_data as any)?.announcementId as string | undefined) ?? message.id
                    : undefined;

                  // Announcement card — custom red gradient card matching ClubChatMinimal
                  if (isAnnouncement) {
                    const priority = (message.message_data as any)?.priority as string | undefined;
                    const title = (message.message_data as any)?.title as string | undefined;
                    const badgeBg = 'rgba(255,255,255,0.25)';
                    return (
                      <AnnouncementObserverRow
                        key={message.id}
                        announcementId={announcementId}
                        viewedRef={viewedAnnouncementsRef}
                        className="relative w-full"
                        style={{
                          marginBottom: message.reaction_counts && Object.keys(message.reaction_counts).some(k => Number((message.reaction_counts as Record<string, number>)[k]) > 0) ? '16px' : '4px',
                        }}
                      >
                        <div
                          className="w-full rounded-2xl overflow-hidden select-none"
                          onTouchStart={(e) => handleLongPressStart(message.id, e)}
                          onTouchEnd={handleLongPressEnd}
                          onMouseDown={(e) => handleLongPressStart(message.id, e)}
                          onMouseUp={handleLongPressEnd}
                          onMouseLeave={handleLongPressEnd}
                          style={{
                            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                            boxShadow: '0 4px 16px rgba(220,38,38,0.25), 0 1px 0 rgba(255,255,255,0.12) inset',
                            opacity: longPressedMessage === message.id ? 0 : 1,
                          }}
                        >
                          {/* Header row */}
                          <div className="flex items-center gap-2 px-4 pt-3 pb-1.5">
                            <div className="w-7 h-7 flex items-center justify-center flex-shrink-0" style={{ borderRadius: 8, background: badgeBg }}>
                              <Megaphone className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0 flex items-center gap-2">
                              <span className="text-[11px] font-bold uppercase tracking-widest text-white/70">
                                {t.clubCommunity?.clubAnnouncement ?? 'Club Announcement'}
                              </span>
                              {priority && (
                                <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full" style={{ background: badgeBg, color: 'rgba(255,255,255,0.9)' }}>
                                  {priority}
                                </span>
                              )}
                            </div>
                            <span className="text-[11px] text-white/50 flex-shrink-0">
                              {formatDateWithLocale(new Date(message.created_at))}
                            </span>
                          </div>
                          {/* Title */}
                          {title && (
                            <div className="px-4 pb-1">
                              <p className="text-[15px] font-bold text-white leading-snug">{title}</p>
                            </div>
                          )}
                          {/* Body */}
                          <div className="px-4 pb-3">
                            <p className="text-sm leading-relaxed text-white/90">{message.content}</p>
                          </div>
                          {/* Thread footer */}
                          {isChannelMode && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setActiveAnnouncementThread(message); }}
                              className="w-full flex items-center gap-2 px-4 py-2.5 active:opacity-80 transition-opacity"
                              style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}
                            >
                              {(announcementReplyCounts[message.id]?.replierIds ?? []).slice(0, 3).map((uid, i) => {
                                const member = channelData.clubMembers?.find(m => m.id === uid);
                                return (
                                  <div key={uid} className="w-5 h-5 rounded-full border border-white/40 bg-white/20 overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ marginLeft: i === 0 ? 0 : -4 }}>
                                    {member?.avatar_url
                                      ? <img src={member.avatar_url} className="w-full h-full object-cover" alt="" />
                                      : <span className="text-[8px] text-white font-bold">{(member?.display_name ?? '?')[0].toUpperCase()}</span>}
                                  </div>
                                );
                              })}
                              <span className="flex-1 text-left text-[12px] font-medium text-white/70">
                                {(() => {
                                  const count = announcementReplyCounts[message.id]?.count ?? 0;
                                  if (count === 0) return t.clubCommunity?.viewThread ?? 'View thread';
                                  return `${count} ${count === 1 ? (t.clubCommunity?.threadReply ?? 'reply') : (t.clubCommunity?.threadReplies ?? 'replies')}`;
                                })()}
                              </span>
                              <ChevronRight className="w-3.5 h-3.5 text-white/50" />
                            </button>
                          )}
                        </div>
                        {/* Reactions — WhatsApp-style white pill overlapping bottom of card, left-aligned */}
                        {message.reaction_counts && Object.keys(message.reaction_counts).some(k => Number((message.reaction_counts as Record<string, number>)[k]) > 0) && (
                          <div className="absolute -bottom-3 left-2 flex items-center gap-0.5 z-10">
                            <div
                              className="flex items-center gap-1 px-2 py-0.5 rounded-full"
                              style={{
                                background: 'rgba(255,255,255,0.97)',
                                border: '1.5px solid rgba(15,23,42,0.09)',
                                boxShadow: '0 2px 8px rgba(15,23,42,0.13)',
                              }}
                            >
                              {Object.entries(message.reaction_counts as Record<string, number>)
                                .filter(([, count]) => Number(count) > 0)
                                .map(([emoji, count]) => (
                                  <button
                                    key={emoji}
                                    onClick={(e) => { e.stopPropagation(); handleReaction(message.id, emoji); }}
                                    disabled={reactingMessages.has(message.id)}
                                    className={`flex items-center gap-0.5 transition-all duration-150 hover:scale-110 active:scale-95 disabled:opacity-50 ${(message.user_has_reacted as any)?.[emoji] ? 'opacity-100' : 'opacity-80'}`}
                                  >
                                    <span className="text-sm leading-none">{emoji}</span>
                                    {Number(count) > 1 && (
                                      <span className="text-[11px] font-semibold text-slate-600 leading-none">{count}</span>
                                    )}
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}
                        {/* Same context menu as normal messages — with lifted card clone */}
                        {longPressedMessage === message.id && (
                          <MessageContextMenu
                            messageId={message.id}
                            isOwnMessage={message.sender_id === user?.id}
                            messageContent={message.content || ''}
                            onReply={() => {
                              const msg = replyToMessage(message.id);
                              if (msg) setReplyTo(msg);
                              setLongPressedMessage(null);
                              setMessagePosition(null);
                            }}
                            onEdit={() => {}}
                            onDelete={() => {}}
                            onCopy={() => navigator.clipboard.writeText(message.content || '')}
                            onReaction={(emoji) => handleReaction(message.id, emoji)}
                            onDismiss={dismissContextMenu}
                            bubbleRect={messagePosition || undefined}
                            liftedBubble={(
                              <div
                                className="w-full rounded-2xl overflow-hidden"
                                style={{
                                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                  boxShadow: '0 4px 16px rgba(220,38,38,0.25), 0 1px 0 rgba(255,255,255,0.12) inset',
                                }}
                              >
                                <div className="flex items-center gap-2 px-4 pt-3 pb-1.5">
                                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0" style={{ borderRadius: 8, background: 'rgba(255,255,255,0.25)' }}>
                                    <Megaphone className="w-4 h-4 text-white" />
                                  </div>
                                  <div className="flex-1 min-w-0 flex items-center gap-2">
                                    <span className="text-[11px] font-bold uppercase tracking-widest text-white/70">
                                      {t.clubCommunity?.clubAnnouncement ?? 'Club Announcement'}
                                    </span>
                                    {priority && (
                                      <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.9)' }}>
                                        {priority}
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-[11px] text-white/50 flex-shrink-0">
                                    {formatDateWithLocale(new Date(message.created_at))}
                                  </span>
                                </div>
                                {title && (
                                  <div className="px-4 pb-1">
                                    <p className="text-[15px] font-bold text-white leading-snug">{title}</p>
                                  </div>
                                )}
                                <div className="px-4 pb-3">
                                  <p className="text-sm leading-relaxed text-white/90">{message.content}</p>
                                </div>
                              </div>
                            )}
                          />
                        )}
                      </AnnouncementObserverRow>
                    );
                  }

                  return (
                    <UnifiedMessageBubble
                      message={message}
                      isOwn={isOwnMessage}
                      isFirstInGroup={isFirstInGroup}
                      isLastInGroup={isLastInGroup}
                      isMiddleInGroup={isMiddleInGroup}
                      isEditing={isEditing}
                      editContent={editContent}
                      onEditChange={setEditContent}
                      onSaveEdit={handleSaveEdit}
                      onCancelEdit={cancelEditing}
                      showReactionPicker={false}
                      longPressedMessage={longPressedMessage}
                      reactingMessages={reactingMessages}
                      onReaction={handleReaction}
                      onEdit={(messageId) => handleEditMessage(messageId, message.content || '')}
                      onDelete={handleDeleteMessage}
                      onReply={(messageId) => {
                        const msg = replyToMessage(messageId);
                        if (msg) {
                          setReplyTo(msg);
                        }
                      }}
                      onConvertToTask={onConvertToTask}
                      onLongPressStart={handleLongPressStart}
                      onLongPressEnd={handleLongPressEnd}
                      onMessageTap={handleMessageTap}
                      onDismissContextMenu={dismissContextMenu}
                      showTimestamp={tappedMessageForTimestamp === message.id}
                      allMessages={activeMessages}
                      language={language}
                      bubbleRect={longPressedMessage === message.id ? messagePosition || undefined : undefined}
                    />
                  );
                })()}
                </React.Fragment>
              );
            })}
            {useVirtualScroll && visibleRange && (
              <div style={{ height: Math.max(0, filteredMessages.length - 1 - visibleRange.endIndex) * ESTIMATED_ROW_HEIGHT }} />
            )}

            {/* Old inline message rendering - replaced by UnifiedMessageBubble component */}
            {false && filteredMessages.map((message, index) => {
              const isOwnMessage = message.sender_id === user?.id;
              const isEditing = editingMessage === message.id;
              const isFirstInGroup = index === 0 || messages[index - 1]?.sender_id !== message.sender_id;
              const isLastInGroup = index === messages.length - 1 || messages[index + 1]?.sender_id !== message.sender_id;
              const isMiddleInGroup = !isFirstInGroup && !isLastInGroup;


              return (
                <div key={message.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-1`}>
                  {/* Avatar - only show for first message in group from others */}
                  {!isOwnMessage && isFirstInGroup ? (
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={message.sender?.avatar_url || undefined} />
                        <AvatarFallback className="bg-gray-300 text-gray-600 text-xs font-medium shadow-sm">
                          {message.sender?.display_name?.[0] || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  ) : !isOwnMessage ? (
                    // Spacer for subsequent messages to align with avatar position (w-8 + mr-3 = 44px = ml-11)
                    <div className="w-11 flex-shrink-0"></div>
                  ) : null}
                  
                  {/* Message bubble and interactions container */}
                  <div className={`max-w-xs lg:max-w-md ${isOwnMessage ? 'ml-auto' : 'mr-auto'}`}>
                    {/* Message content or edit input */}
                    {isEditing && editingMessage === message.id ? (
                      <div className="bg-white border border-gray-200 rounded-2xl p-3 shadow-sm">
                        <input
                          type="text"
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1B4332] focus:border-transparent"
                          autoFocus
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') cancelEditing();
                          }}
                        />
                        <div className="flex space-x-2 mt-2">
                          <button
                            onClick={handleSaveEdit}
                            className="px-3 py-1 bg-gradient-to-r from-[#2A4494] to-[#1E3270] text-white text-xs rounded-full hover:from-[#1E3270] hover:to-[#2A4494] transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded-full hover:bg-gray-300 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="relative"
                        data-message-id={message.id}
                        onTouchStart={(e) => handleLongPressStart(message.id, e)}
                        onTouchEnd={handleLongPressEnd}
                        onMouseDown={(e) => handleLongPressStart(message.id, e)}
                        onMouseUp={handleLongPressEnd}
                        onMouseLeave={handleLongPressEnd}
                        onClick={() => handleMessageTap(message.id)}
                      >
                        
                        <div
                          className={`relative group cursor-pointer message-bubble ${
                            isOwnMessage 
                              ? `text-white shadow-lg hover:shadow-xl ${
                                  isFirstInGroup ? 'rounded-2xl rounded-br-md' : 
                                  isLastInGroup ? 'rounded-2xl rounded-bl-md' : 
                                  'rounded-2xl'
                                }` 
                              : `bg-white text-gray-900 shadow-md hover:shadow-lg border border-gray-100 ${
                                  isFirstInGroup ? 'rounded-2xl rounded-bl-md' : 
                                  isLastInGroup ? 'rounded-2xl rounded-br-md' : 
                                  'rounded-2xl'
                                }`
                          } px-4 py-1 transition-all duration-200 ease-out transform hover:scale-[1.01] active:scale-[0.99]`}
                          style={isOwnMessage ? {
                            background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)',
                            boxShadow: '0 4px 12px rgba(42, 68, 148, 0.15)'
                          } : {
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                          }}
                        >
                          {/* Photo message */}
                          {message.message_type === 'photo' ? (
                            <div className="space-y-3">
                              {/* Reply Preview in Photo Message */}
                              {message.reply_to_id ? (() => {
                                  const originalMessage = messages.find(m => m.id === message.reply_to_id);
                                  return originalMessage ? (
                                    <div className={`p-2 rounded-lg border-l-4 cursor-pointer transition-all duration-200 hover:shadow-md ${isOwnMessage
                                      ? 'bg-white/20 border-white/60 shadow-sm'
                                      : 'bg-blue-50 border-blue-300 shadow-sm'
                                    }`}
                                      onClick={() => {
                                      const element = document.querySelector(`[data-message-id="${originalMessage.id}"]`);
                                      if (element) {
                                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        element.style.backgroundColor = '#fef3c7';
                                        setTimeout(() => {
                                          element.style.backgroundColor = '';
                                        }, 1500);
                                      }
                                    }}
                                    >
                                      <div className={`text-xs font-semibold mb-1 flex items-center ${isOwnMessage ? 'text-white/90' : 'text-blue-700'}`}>
                                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 717 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        {originalMessage.sender?.display_name || 'Unknown User'}
                                      </div>
                                      <div className={`text-xs italic ${isOwnMessage ? 'text-white/80' : 'text-blue-600'}`}>
                                        "{originalMessage.content?.substring(0, 60) || 'Original message'}{originalMessage.content && originalMessage.content.length > 60 ? '...' : ''}"
                                      </div>
                                    </div>
                                  ) : null;
                                })() : null}

                              {/* Image container with better styling */}
                              <div className="relative group">
                                {(message.message_data as any)?.image_urls?.map((url: string, index: number) => (
                                  <div key={index} className="relative overflow-hidden rounded-xl">
                                    <img 
                                      src={url} 
                                      alt={t.messaging.sharedPhoto}
                                      className="w-full h-auto object-cover rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                                      loading="lazy"
                                      style={{
                                        maxHeight: '300px',
                                        minHeight: '200px'
                                      }}
                                    />
                                    {/* Subtle overlay on hover */}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300 rounded-xl" />
                                  </div>
                                ))}
                              </div>
                              
                              {/* Caption with better styling */}
                              {message.content ? <div className={`text-sm leading-relaxed ${isOwnMessage ? 'text-white' : 'text-gray-900'} px-1`}>
                                {message.content}
                                                 </div> : null}
                            </div>
                          ) : (
                            /* Regular text message */
                            <div className={`text-sm leading-relaxed ${isOwnMessage ? 'text-white' : 'text-gray-900'}`}>
                              {/* Reply Preview in Message */}
                              {message.reply_to_id ? (() => {
                                  const originalMessage = messages.find(m => m.id === message.reply_to_id);
                                  return originalMessage ? (
                                    <div className={`mb-2 p-2 rounded-lg border-l-4 cursor-pointer transition-all duration-200 hover:shadow-md ${isOwnMessage
                                      ? 'bg-white/20 border-white/60 shadow-sm'
                                      : 'bg-blue-50 border-blue-300 shadow-sm'
                                    }`}
                                      onClick={() => {
                                      const element = document.querySelector(`[data-message-id="${originalMessage.id}"]`);
                                      if (element) {
                                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        element.style.backgroundColor = '#fef3c7';
                                        setTimeout(() => {
                                          element.style.backgroundColor = '';
                                        }, 1500);
                                      }
                                    }}
                                    >
                                      <div className={`text-xs font-semibold mb-1 flex items-center ${isOwnMessage ? 'text-white/90' : 'text-blue-700'}`}>
                                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        {originalMessage.sender?.display_name || 'Unknown User'}
                                      </div>
                                      <div className={`text-xs italic ${isOwnMessage ? 'text-white/80' : 'text-blue-600'}`}>
                                        "{originalMessage.content?.substring(0, 60) || 'Original message'}{originalMessage.content && originalMessage.content.length > 60 ? '...' : ''}"
                                      </div>
                                    </div>
                                  ) : null;
                                })() : null}

                              {message.content}
                              {message.edited_at ? <span className={`text-xs ml-2 opacity-75 ${isOwnMessage ? 'text-blue-100' : 'text-gray-500'}`}>
                                (edited)
                              </span> : null}
                            </div>
                          )}
                          
                          {/* Subtle indicator for long press */}
                          <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none ${
                            isOwnMessage ? 'bg-white/10' : 'bg-gray-50/50'
                          }`}
                          />
                          
                          {/* Timestamp - show on hover or for last message in group */}
                          {(isLastInGroup || isOwnMessage) ? <div className={`absolute bottom-0 text-xs opacity-0 group-hover:opacity-75 transition-opacity duration-200 pointer-events-none ${
                              isOwnMessage 
                                ? 'text-white/80 -bottom-5 right-0' 
                                : 'text-gray-500 -bottom-5 left-0'
                            }`}
                                                             >
                            {new Date(message.created_at).toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                                                             </div> : null}
                        </div>
                        
                        {/* Context Menu - only show for this specific message */}
                        {longPressedMessage === message.id && (
                          <div 
                            className="absolute z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 min-w-[200px] py-2 animate-slide-up"
                            style={{
                              top: '100%',
                              left: '0',
                              marginTop: '8px'
                            }}
                            data-message-id={message.id}
                          >
                            {/* Reply */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const msg = replyToMessage(message.id);
                                if (msg) setReplyTo(msg);
                                setShowReactionPicker(null);
                                setLongPressedMessage(null);
                                setMessagePosition(null);
                              }}
                              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-150"
                            >
                              <span className="text-gray-900 font-medium">{t.messaging.reply}</span>
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                              </svg>
                            </button>
                            
                            {/* Edit — own messages only */}
                            {isOwnMessage && <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditMessage(message.id, message.content);
                                setShowReactionPicker(null);
                                setLongPressedMessage(null);
                                setMessagePosition(null);
                              }}
                              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-150"
                            >
                              <span className="text-gray-900 font-medium">{t.messaging.edit}</span>
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>}
                            
                            {/* Copy */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(message.content);
                                setShowReactionPicker(null);
                                setLongPressedMessage(null);
                                setMessagePosition(null);
                              }}
                              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-150"
                            >
                              <span className="text-gray-900 font-medium">{t.messaging.copy}</span>
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                            
                            {/* Convert to Task — admin only, shown when prop is provided */}
                            {onConvertToTask && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onConvertToTask({ id: message.id, content: message.content, sender_name: message.sender?.display_name });
                                  setShowReactionPicker(null);
                                  setLongPressedMessage(null);
                                  setMessagePosition(null);
                                }}
                                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-150"
                              >
                                <span className="text-gray-900 font-medium">Convert to Task</span>
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                </svg>
                              </button>
                            )}

                            {/* Separator + Delete — own messages only */}
                            {isOwnMessage && <>
                              <div className="border-t border-gray-200 my-1" />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteMessage(message.id);
                                  setShowReactionPicker(null);
                                  setLongPressedMessage(null);
                                  setMessagePosition(null);
                                }}
                                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-red-50 transition-colors duration-150"
                              >
                                <span className="text-red-600 font-medium">{t.messaging.delete}</span>
                                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>}
                          </div>
                        )}
                        
                        {/* Reactions display */}
                        {message.reaction_counts && Object.keys(message.reaction_counts).length > 0 ? <div className={`flex flex-wrap gap-1 mt-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                          {Object.entries(message.reaction_counts).map(([emoji, count]) => 
                              count > 0 ? (
                                <button 
                                  key={emoji}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReaction(message.id, emoji);
                                  }}
                                  disabled={reactingMessages.has(message.id)}
                                  className={`text-xs px-3 py-1.5 rounded-full transition-all duration-150 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${
                                    message.user_has_reacted?.[emoji] 
                                      ? 'bg-blue-100 border border-blue-200 text-[#2A4494] shadow-sm' 
                                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  {reactingMessages.has(message.id) ? (
                                    <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    `${emoji} ${count}`
                                  )}
                                </button>
                              ) : null
                            )}
                                                                                                      </div> : null}
                        
                        {/* Timestamp */}
                        {isLastInGroup ? <div className={`text-xs text-gray-500 mt-1 ${isOwnMessage ? 'text-right' : 'text-left'}`}>
                          {formatDateWithLocale(new Date(message.created_at))}
                                         </div> : null}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {isChannelMode && channelData.typingUsers.length > 0 ? (
          <div style={{ padding: '4px 16px', color: '#9ca3af', fontSize: 12 }}>
            {channelData.typingUsers.map((u: any) => u.display_name || u.user_id).join(', ')} {channelData.typingUsers.length === 1 ? 'is' : 'are'} typing…
          </div>
        ) : isTyping ? <div className="flex items-end space-x-2 animate-fade-in">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center shadow-sm">
            <span className="text-xs text-gray-500">...</span>
          </div>
          <div className="bg-white rounded-2xl rounded-bl-md p-4 shadow-sm border border-gray-100">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            </div>
          </div>
                    </div> : null}
        
        {loadingMore ? <div className="flex justify-center py-4">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                       </div> : null}
        
        
        {/* Show offline messages */}
        {offlineMessages.map((offlineMessage) => (
          <div key={offlineMessage.id} className="flex justify-end mb-1">
            <div className="max-w-xs lg:max-w-md ml-auto">
              <div 
                className="relative group cursor-pointer message-bubble bg-gradient-to-br from-gray-400 to-gray-500 text-white rounded-2xl rounded-br-md shadow-lg px-4 py-3 transition-all duration-200 ease-out opacity-75"
                style={{
                  background: 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)'
                }}
              >
                <div className="text-sm leading-relaxed">
                  {offlineMessage.content}
                </div>
                <div className="absolute -top-2 -right-2 w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center">
                  <span className="text-xs">⏳</span>
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1 text-right">
                Queued for sending
              </div>
            </div>
          </div>
        ))}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Photo preview */}
      <PhotoUploadPreview
        photos={selectedPhotos}
        previewUrls={photoPreviewUrls}
        onRemove={removePhoto}
        onClear={clearPhotos}
      />

      {/* Exercise context chip — shown when navigating from Ask Coach */}
      {pendingExercise && (
        <div className="px-3 pb-1 flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl text-[12px] font-medium" style={{ background: 'rgba(42,68,148,0.08)', border: '1px solid rgba(42,68,148,0.18)', color: '#2A4494' }}>
            <span style={{ fontSize: 13 }}>📎</span>
            <span className="truncate">{String(pendingExercise.exercise_name ?? 'Exercise')} will be attached</span>
          </div>
          <button
            onClick={() => setPendingExercise(undefined)}
            className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full"
            style={{ background: 'rgba(15,23,42,0.08)', color: '#64748B' }}
            aria-label="Remove exercise attachment"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* @mention autocomplete (channel mode only) */}
      {isChannelMode ? (
        <div style={{ position: 'relative' }}>
          <MentionAutocomplete
            members={channelData.clubMembers}
            inputValue={messageInput}
            cursorPosition={cursorPosition}
            onSelect={handleMentionSelect}
          />
        </div>
      ) : null}

      {/* Message input — flex-shrink-0 so it's never squished by the messages area */}
      <div className="flex-shrink-0">
      <UnifiedMessageInput
        ref={messageInputRef}
        value={messageInput}
        onChange={(value: string) => {
          setMessageInput(value);
          if (isChannelMode && channelData.activeChannel) {
            clubCommunityService.sendTypingIndicator(channelData.activeChannel);
          }
        }}
        onSend={async () => {
          if (isChannelMode) {
            if (selectedPhotos.length > 0 && channelData.activeChannel) {
              try {
                const photoUrls = await uploadMultiplePhotos(selectedPhotos);
                if (photoUrls.length > 0) {
                  channelData.sendMessage({
                    channel_id: channelData.activeChannel,
                    content: messageInput,
                    message_type: 'photo',
                    message_data: { image_urls: photoUrls },
                    reply_to_id: replyTo?.id,
                  });
                  clearPhotos();
                  setMessageInput('');
                  setReplyTo(null);
                }
              } catch (e) {
                console.error('Failed to send photo in channel:', e);
              }
            } else if (messageInput.trim() && channelData.activeChannel) {
              channelData.sendMessage({
                channel_id: channelData.activeChannel,
                content: messageInput,
                message_type: 'text',
                reply_to_id: replyTo?.id,
              });
              setMessageInput('');
              setReplyTo(null);
            }
          } else if (selectedPhotos.length > 0) {
            handleSendPhotos();
          } else if (messageInput.trim()) {
            if (pendingExercise) {
              handleSendMessage(messageInput, 'exercise_share', pendingExercise, replyTo?.id);
              setPendingExercise(undefined);
            } else {
              handleSendMessage(messageInput, 'text', undefined, replyTo?.id);
            }
          }
        }}
        onPhotoSelect={handlePhotoSelect}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        selectedPhotos={selectedPhotos}
        sending={sending}
        uploading={uploading || voiceUploading}
        disabled={sending || uploading || voiceUploading || videoUploading}
        isOnline={isOnline}
        uploadProgress={voiceUploading ? voiceUploadProgress : uploadProgress}
        onVideoSelect={handleVideoSelect}
        onVideoLinkSend={handleVideoLinkSend}
        videoUploading={videoUploading}
        videoUploadProgress={videoUploadProgress}
        // Voice recording props
        isRecording={voiceRecorder.isRecording}
        recordingDuration={voiceRecorder.duration}
        audioBlob={voiceRecorder.audioBlob}
        audioUrl={voiceRecorder.audioUrl}
        voiceError={voiceRecorder.error}
        onStartRecording={voiceRecorder.startRecording}
        onStopRecording={voiceRecorder.stopRecording}
        onCancelRecording={voiceRecorder.cancelRecording}
        onSendVoice={handleSendVoice}
        onCursorChange={isChannelMode ? setCursorPosition : undefined}
        channelName={isChannelMode ? (channelData.channels.find(c => c.id === channelData.activeChannel)?.name) : undefined}
      />
      </div>

      {isChannelMode && activeAnnouncementThread && channelData.activeChannel && (
        <AnnouncementThreadSheet
          isOpen={!!activeAnnouncementThread}
          onClose={() => {
            setActiveAnnouncementThread(null);
            const ids = channelData.messages
              .filter(m => !!(m.message_data as any)?.isAnnouncement)
              .map(m => m.id);
            if (ids.length && channelData.activeChannel) {
              clubCommunityService.getAnnouncementReplyCounts(ids, channelData.activeChannel)
                .then(r => { if (r.success && r.data) setAnnouncementReplyCounts(r.data); });
            }
          }}
          announcement={activeAnnouncementThread}
          channelId={channelData.activeChannel}
          channelName={channelData.channels.find(c => c.id === channelData.activeChannel)?.name}
        />
      )}

      {/* Old inline message input - replaced by UnifiedMessageInput component */}
      {false && <div className="bg-white border-t border-gray-200 p-4 safe-area-bottom">
        <div className="flex items-center space-x-3">
          {/* Photo upload button */}
          <button 
            onClick={() => photoInputRef.current?.click()}
            className="p-3 text-gray-400 hover:text-[#2A4494] transition-all duration-300 hover:bg-blue-50 rounded-full hover:scale-110 active:scale-95 shadow-sm hover:shadow-md"
          >
            <Camera className="h-5 w-5" />
          </button>
          
          {/* Hidden file input */}
          <input
            ref={photoInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => handlePhotoSelect(e.target.files)}
            className="hidden"
          />
          
          <div className="flex-1 relative">
            <input
              ref={messageInputRef}
              type="text"
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={!isOnline ? t.messaging.messageWillBeSentOffline : t.messaging.typeMessage}
              className="w-full px-4 py-3 border border-gray-200 rounded-full text-sm focus:ring-2 focus:ring-[#1B4332] focus:border-transparent transition-all duration-300 shadow-sm hover:shadow-md focus:shadow-lg bg-white"
            />
            {!isOnline && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              </div>
            )}
          </div>
          
          <button
            onClick={async () => {
              if (isChannelMode) {
                if (selectedPhotos.length > 0 && channelData.activeChannel) {
                  try {
                    const photoUrls = await uploadMultiplePhotos(selectedPhotos);
                    if (photoUrls.length > 0) {
                      channelData.sendMessage({ channel_id: channelData.activeChannel, content: messageInput, message_type: 'photo', message_data: { image_urls: photoUrls }, reply_to_id: replyTo?.id });
                      clearPhotos(); setMessageInput(''); setReplyTo(null);
                    }
                  } catch (e) { console.error('Failed to send photo in channel:', e); }
                } else if (messageInput.trim() && channelData.activeChannel) {
                  channelData.sendMessage({ channel_id: channelData.activeChannel, content: messageInput, message_type: 'text', reply_to_id: replyTo?.id });
                  setMessageInput(''); setReplyTo(null);
                }
              } else if (selectedPhotos.length > 0) {
                handleSendPhotos();
              } else {
                handleSendMessage(messageInput, 'text', undefined, replyTo?.id);
              }
            }}
            disabled={(!messageInput.trim() && selectedPhotos.length === 0) || uploading || sending}
            className="p-3 bg-gradient-to-r from-[#2A4494] to-[#1E3270] text-white rounded-full hover:from-[#1E3270] hover:to-[#2A4494] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110 active:scale-95 disabled:transform-none"
          >
            {(uploading || sending) ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </div>
        
        {/* Upload progress - handled by UnifiedMessageInput */}
        
        {/* Scroll to bottom button */}
        {showScrollToBottom ? <button
          onClick={scrollToBottom}
          className="fixed bottom-20 right-4 w-12 h-12 bg-white border border-gray-200 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center z-10 animate-fade-in"
                              >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
                              </button> : null}
      </div>}

      {/* Edit Message Modal */}
      {editingMessage ? <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
        <div className="w-full max-w-md mx-4 mb-8 bg-white rounded-2xl shadow-2xl border border-gray-100 animate-slide-up">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">{t.messaging.editMessage}</h3>
          </div>
            
          {/* Input Area */}
          <div className="p-6">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder={t.messaging.typeYourMessage}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1B4332] focus:border-transparent transition-all duration-200 resize-none"
              rows={3}
              style={{
                  minHeight: '80px',
                  maxHeight: '120px'
                }}
            />
          </div>
            
          {/* Action Buttons */}
          <div className="flex gap-3 px-6 pb-6">
            <button
              onClick={cancelEditing}
              className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors duration-150 active:scale-95"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={!editContent.trim()}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-[#2A4494] to-[#1E3270] text-white font-medium rounded-xl hover:from-[#1E3270] hover:to-[#2A4494] transition-all duration-150 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none relative z-10"
              style={{
                  background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)',
                  color: 'white',
                  fontWeight: '500'
                }}
            >
              Save
            </button>
          </div>
        </div>
                        </div> : null}

      {/* Confirmation Dialogs */}
      
      {/* {t.messaging.clearChat} Confirmation */}
      {showClearChatConfirm ? <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in px-4">
        <div
          className="w-full max-w-sm rounded-2xl overflow-hidden animate-slide-up"
          style={{
            background: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(32px) saturate(180%)',
            WebkitBackdropFilter: 'blur(32px) saturate(180%)',
            border: '1px solid rgba(15,23,42,0.08)',
            boxShadow: '0 0 0 0.5px rgba(255,255,255,0.9) inset, 0 24px 64px rgba(15,23,42,0.22), 0 4px 12px rgba(15,23,42,0.10)',
          }}
        >
          <div className="p-6">
            <h3 className="text-[15px] font-bold text-slate-900 tracking-tight mb-1.5">{t.messaging.clearChat}</h3>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              This will delete all messages in this conversation. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearChatConfirm(false)}
                className="flex-1 px-4 py-3 text-slate-700 font-medium rounded-xl transition-colors duration-150 hover:bg-slate-100"
                style={{ background: 'rgba(15,23,42,0.05)', border: '1px solid rgba(15,23,42,0.08)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleClearChat}
                className="flex-1 px-4 py-3 text-white font-medium rounded-xl transition-colors duration-150 hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', boxShadow: '0 4px 12px rgba(239,68,68,0.3)' }}
              >
                {t.messaging.clearChat}
              </button>
            </div>
          </div>
        </div>
                              </div> : null}

      {/* Block/Leave Confirmation */}
      {showBlockConfirm ? <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in px-4">
        <div
          className="w-full max-w-sm rounded-2xl overflow-hidden animate-slide-up"
          style={{
            background: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(32px) saturate(180%)',
            WebkitBackdropFilter: 'blur(32px) saturate(180%)',
            border: '1px solid rgba(15,23,42,0.08)',
            boxShadow: '0 0 0 0.5px rgba(255,255,255,0.9) inset, 0 24px 64px rgba(15,23,42,0.22), 0 4px 12px rgba(15,23,42,0.10)',
          }}
        >
          <div className="p-6">
            <h3 className="text-[15px] font-bold text-slate-900 tracking-tight mb-1.5">
              {conversationType === 'direct' ? t.messaging.blockUser : t.messaging.leaveChannel}
            </h3>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              {conversationType === 'direct'
                  ? 'This will block the user and remove them from your conversations. You can unblock them later in settings.'
                  : "This will remove you from this channel. You can rejoin later if it's a public channel."
                }
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBlockConfirm(false)}
                className="flex-1 px-4 py-3 text-slate-700 font-medium rounded-xl transition-colors duration-150 hover:bg-slate-100"
                style={{ background: 'rgba(15,23,42,0.05)', border: '1px solid rgba(15,23,42,0.08)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleBlockOrLeave}
                className="flex-1 px-4 py-3 text-white font-medium rounded-xl transition-colors duration-150 hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', boxShadow: '0 4px 12px rgba(239,68,68,0.3)' }}
              >
                {conversationType === 'direct' ? t.messaging.blockUser : t.messaging.leaveChannel}
              </button>
            </div>
          </div>
        </div>
                          </div> : null}
    </div>
  );
} 